import React, { useState, useEffect, useMemo, useCallback, Fragment } from 'react';
import { db, storage, functions as firebaseFunctions } from '../firebase-config';
import { 
  collection, query, where, onSnapshot, doc, getDoc, updateDoc, 
  addDoc, arrayUnion, arrayRemove, serverTimestamp, orderBy, runTransaction, deleteField, Timestamp, limit
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import ServiceAreaMap from './ServiceAreaMap';
import AddressAutocomplete from './AddressAutocomplete';
import FeedbackModal from './FeedbackModal';
import DashboardAppearanceSettings from './DashboardAppearanceSettings';
import {
  MAX_PROFESSIONAL_MEETING_PROPOSALS,
  normalizeMeetingProposals,
  countProposalProfessionals,
  getProposalForProfessional,
  formatMeetingOptionForViewer,
  sanitizeProfessionalMeetingProposalsForWrite,
} from '../utils/maintenanceMeetingProposals';
import {
  effectiveProfessionalPlatformFeePercent,
  grossJobAmountFromMaintenanceRequest,
  professionalNetPayoutDollars,
} from '../utils/proMaintenancePayout';
import { geocodeAddressString } from '../utils/geocodeNominatim';

const REPAIR_CATEGORIES = [
  'Plumbing', 'Electrical', 'Heating/Cooling', 'Roofing', 'Flooring',
  'Painting', 'Drywall', 'Appliances', 'Windows/Doors', 'General Maintenance (Handyman)',
  'Pest Control', 'Landscaping', 'Cleaning', 'Other'
];

/** Must match key set in ProfessionalLandingPage.js hero optional notes. */
const PRO_LANDING_SIGNUP_NOTES_KEY = 'proLandingSignupNotes';

const TRADE_META = {
  Plumbing: { icon: '🔧', requiresLicense: true, tradeType: 'licensed' },
  Electrical: { icon: '⚡', requiresLicense: true, tradeType: 'licensed' },
  'Heating/Cooling': { icon: '❄️', requiresLicense: true, tradeType: 'licensed' },
  Roofing: { icon: '🏠', requiresLicense: true, tradeType: 'licensed' },
  'Pest Control': { icon: '🐛', requiresLicense: true, tradeType: 'licensed' },
  'General Maintenance (Handyman)': { icon: '🛠️', requiresLicense: false, tradeType: 'handyman' },
  Painting: { icon: '🎨', requiresLicense: false, tradeType: 'general' },
  Drywall: { icon: '🧱', requiresLicense: false, tradeType: 'general' },
  Flooring: { icon: '🪵', requiresLicense: false, tradeType: 'general' },
  Appliances: { icon: '🔌', requiresLicense: false, tradeType: 'general' },
  'Windows/Doors': { icon: '🪟', requiresLicense: false, tradeType: 'general' },
  Landscaping: { icon: '🌿', requiresLicense: false, tradeType: 'general' },
  Cleaning: { icon: '🧹', requiresLicense: false, tradeType: 'general' },
  Other: { icon: '📦', requiresLicense: false, tradeType: 'general' },
};

/** Must match `REPAIR_CATEGORIES` / onboarding — handymen see the full open-request pool. */
const HANDYMAN_TRADE_LABEL = 'General Maintenance (Handyman)';

/**
 * Whether a maintenance request should appear in this professional's "available to claim" list.
 * Pros who selected Handyman see all categories (owner/tenant requests often use slug values like `plumbing`).
 */
function professionalSeesRequestCategory(profCategories, requestCategory) {
  const cats = Array.isArray(profCategories) ? profCategories : [];
  const proLower = cats.map((c) => String(c).trim().toLowerCase());
  if (proLower.includes(HANDYMAN_TRADE_LABEL.toLowerCase())) return true;

  const req = String(requestCategory ?? '').trim().toLowerCase();
  if (req === 'other') return true;
  if (!req) return false;
  return proLower.includes(req);
}

/** Owner-first window: `preferredOfferProIds` + `preferredOfferUntil` on `maintenance_requests`. */
function getPreferredOfferProIds(req) {
  const raw = req?.preferredOfferProIds;
  if (!Array.isArray(raw)) return [];
  return [...new Set(raw.map((id) => String(id || '').trim()).filter(Boolean))];
}

function getPreferredOfferUntilMs(req) {
  const t = req?.preferredOfferUntil;
  if (!t) return null;
  if (typeof t.toDate === 'function') return t.toDate().getTime();
  if (t instanceof Date) return t.getTime();
  if (typeof t === 'number' && Number.isFinite(t)) return t;
  if (typeof t === 'string') {
    const d = Date.parse(t);
    return Number.isFinite(d) ? d : null;
  }
  return null;
}

function isPreferredOfferWindowActive(req) {
  if (getPreferredOfferProIds(req).length === 0) return false;
  const until = getPreferredOfferUntilMs(req);
  if (until == null) return false;
  return Date.now() < until;
}

function proIsOnPreferredOfferSnapshot(req, proUid) {
  return getPreferredOfferProIds(req).includes(String(proUid || '').trim());
}

function availableRequestSortScore(req, proUid) {
  const onSnap = proIsOnPreferredOfferSnapshot(req, proUid);
  const windowOn = isPreferredOfferWindowActive(req);
  if (windowOn && onSnap) return 3;
  if (windowOn && !onSnap) return 0;
  if (!windowOn && onSnap) return 2;
  return 1;
}

/** Maps Firestore `maintenance_requests.status` to `request-badge.*` (styled in App.css for light + dark). */
function proRequestStatusBadgeClass(status) {
  const raw = String(status || 'pending')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
  if (raw === 'in_progress' || raw === 'inprogress') return 'in_progress';
  if (raw === 'open') return 'open';
  if (raw === 'scheduled') return 'scheduled';
  if (raw === 'completed') return 'completed';
  if (raw === 'cancelled' || raw === 'canceled') return 'cancelled';
  if (raw === 'pending_review' || raw === 'follow_up') return 'in_progress';
  return 'pending';
}

/**
 * Section header + chevron; body hidden when collapsed. Stops propagation when used inside clickable cards.
 */
function ProCollapsiblePanel({ title, labelStyle, open, onToggle, children, style, stopPropagation, role, ariaLabel }) {
  const stop = stopPropagation ? (e) => e.stopPropagation() : undefined;
  return (
    <div style={style} onClick={stop} role={role} aria-label={ariaLabel}>
      <button
        type="button"
        onClick={(e) => {
          if (stopPropagation) e.stopPropagation();
          onToggle();
        }}
        aria-expanded={open}
        style={{
          display: 'flex',
          width: '100%',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          padding: 0,
          margin: '0 0 10px 0',
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          textAlign: 'left',
          font: 'inherit',
          color: 'inherit',
        }}
      >
        <span style={{ ...labelStyle, margin: 0 }}>{title}</span>
        <span aria-hidden style={{ fontSize: 11, color: '#94a3b8', flexShrink: 0 }}>
          {open ? '▼' : '▶'}
        </span>
      </button>
      {open ? <div onClick={stop}>{children}</div> : null}
    </div>
  );
}

/** Desktop → Google Maps; mobile → system maps (Apple Maps on iOS, geo: on Android). */
function buildProMapsHref(rawAddress) {
  const address = String(rawAddress ?? '').trim();
  if (!address) return '#';
  const q = encodeURIComponent(address);
  if (typeof navigator === 'undefined') {
    return `https://www.google.com/maps/search/?api=1&query=${q}`;
  }
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod/i.test(ua)) {
    return `https://maps.apple.com/?q=${q}`;
  }
  if (/Android/i.test(ua)) {
    return `geo:0,0?q=${q}`;
  }
  return `https://www.google.com/maps/search/?api=1&query=${q}`;
}

const ONBOARDING_STEPS = [
  { num: 1, label: 'Your Trade' },
  { num: 2, label: 'Qualifications' },
  { num: 3, label: 'Business Profile' },
  { num: 4, label: 'Deposit' },
  { num: 5, label: 'All Set' },
];

const YEARS_OPTIONS = ['Less than 1 year', '1–3 years', '3–5 years', '5–10 years', '10+ years'];

const PRO_DEPOSIT_AMOUNT = 20;

/** Paid job → mark complete: require written scope-of-work; photos optional (separate from estimate photos). */
const PAID_JOB_COMPLETION_NOTE_MIN_LEN = 15;
const PAID_JOB_COMPLETION_NOTE_MAX_LEN = 4000;
const PAID_JOB_COMPLETION_MAX_PHOTOS = 8;

/** Firestore may return stars as number or long; accept numeric strings too. */
function normalizeRatingStars(rating) {
  if (!rating || rating.stars == null) return null;
  const n = Number(rating.stars);
  if (!Number.isFinite(n)) return null;
  const i = Math.round(n);
  if (i < 1 || i > 5) return null;
  return i;
}

/**
 * Who opened the request (owner vs tenant) and how to show them.
 * Owner-created docs use `ownerInfo`; tenant-created may use `tenantInfo` / `tenantName` or only `tenantId`.
 */
function getMaintenanceRequesterDisplay(req, resolvedTenantNameById = {}) {
  const oi = req.ownerInfo;
  const ownerCombined = oi
    ? [oi.firstName, oi.lastName].filter(Boolean).join(' ').trim() || (oi.email && String(oi.email).trim()) || ''
    : '';
  const ti = req.tenantInfo;
  const resolvedName = req.tenantId ? resolvedTenantNameById[req.tenantId] : '';
  const tenantCombined =
    (ti?.fullName && String(ti.fullName).trim()) ||
    (req.tenantName && String(req.tenantName).trim()) ||
    (resolvedName && String(resolvedName).trim()) ||
    '';

  if (req.createdBy === 'owner' && ownerCombined) {
    return {
      name: ownerCombined,
      roleLabel: 'Property owner',
      phone: oi?.phone || ti?.phoneNumber || '',
      email: oi?.email || ti?.email || '',
    };
  }
  if (tenantCombined) {
    return {
      name: tenantCombined,
      roleLabel: 'Client',
      phone: ti?.phoneNumber || '',
      email: ti?.email || '',
    };
  }
  if (ownerCombined) {
    return {
      name: ownerCombined,
      roleLabel: 'Property owner',
      phone: oi?.phone || '',
      email: oi?.email || '',
    };
  }
  return {
    name: req.tenantId || req.ownerId ? 'Contact on file' : 'Client',
    roleLabel: 'Contact',
    phone: ti?.phoneNumber || oi?.phone || '',
    email: ti?.email || oi?.email || '',
  };
}

/**
 * Owner + tenant/client lines for professional cards when both sides are on the request.
 */
function getOwnerAndTenantForPro(req, resolvedTenantNameById = {}) {
  const oi = req.ownerInfo;
  const ownerName = oi
    ? [oi.firstName, oi.lastName].filter(Boolean).join(' ').trim() ||
      (oi.email && String(oi.email).trim()) ||
      ''
    : '';
  const ti = req.tenantInfo;
  const resolved =
    (req.tenantId &&
      resolvedTenantNameById[req.tenantId] &&
      String(resolvedTenantNameById[req.tenantId]).trim()) ||
    '';
  const tenantName =
    (ti?.fullName && String(ti.fullName).trim()) ||
    (req.tenantName && String(req.tenantName).trim()) ||
    resolved ||
    '';

  const owner = ownerName
    ? { name: ownerName, phone: oi?.phone || '', email: oi?.email || '' }
    : null;

  let tenant = null;
  if (tenantName) {
    tenant = {
      name: tenantName,
      phone: ti?.phoneNumber || '',
      email: ti?.email || '',
    };
  } else if (req.tenantId) {
    tenant = {
      name: resolved || 'Client',
      phone: ti?.phoneNumber || '',
      email: ti?.email || '',
    };
  }

  return { owner, tenant };
}

const contactSepStyle = { paddingTop: 6, borderTop: '1px solid #e2e8f0', marginTop: 10 };

/** Owner + client rows after address (or standalone on available cards). */
function ProMaintenanceContactRows({ req, resolvedTenantNames, lineStyle, contactAfterAddress }) {
  const baseLine = lineStyle || { margin: '6px 0', fontSize: 14, color: '#334155', lineHeight: 1.5 };
  const { owner, tenant } = getOwnerAndTenantForPro(req, resolvedTenantNames);

  const personBlock = (title, person, index) => {
    if (!person?.name) return null;
    const top =
      (contactAfterAddress && index === 0) || index > 0 ? contactSepStyle : {};
    return (
      <Fragment key={title}>
        <p style={{ ...baseLine, ...top }}>
          <strong>{title}</strong> · {person.name}
        </p>
        {person.phone ? (
          <p style={baseLine}>
            <a href={`tel:${person.phone}`} style={{ color: '#4f46e5', textDecoration: 'none' }}>
              {person.phone}
            </a>
          </p>
        ) : null}
        {person.email ? (
          <p style={baseLine}>
            <a href={`mailto:${person.email}`} style={{ color: '#4f46e5', textDecoration: 'none' }}>
              {person.email}
            </a>
          </p>
        ) : null}
      </Fragment>
    );
  };

  if (owner || tenant) {
    const parts = [];
    if (owner) parts.push(['Property owner', owner]);
    if (tenant) parts.push(['Client', tenant]);
    return <>{parts.map(([title, p], i) => personBlock(title, p, i))}</>;
  }

  const who = getMaintenanceRequesterDisplay(req, resolvedTenantNames);
  return (
    <>
      <p style={{ ...baseLine, ...(contactAfterAddress ? contactSepStyle : {}) }}>
        <strong>{who.roleLabel}</strong> · {who.name}
      </p>
      {who.phone ? (
        <p style={baseLine}>
          <a href={`tel:${who.phone}`} style={{ color: '#4f46e5', textDecoration: 'none' }}>
            {who.phone}
          </a>
        </p>
      ) : null}
      {who.email ? (
        <p style={baseLine}>
          <a href={`mailto:${who.email}`} style={{ color: '#4f46e5', textDecoration: 'none' }}>
            {who.email}
          </a>
        </p>
      ) : null}
    </>
  );
}

/** True once owner/tenant accepted the estimate or payment was captured — pro must not edit. */
function isProfessionalEstimateLocked(req) {
  const est = req.profesionalEstimate;
  if (!est) return false;
  const st = String(est.status || '').toLowerCase();
  if (st === 'accepted' || st === 'paid') return true;
  const ps = String(req.paymentStatus || '').toLowerCase();
  if (ps === 'captured' || ps === 'paid' || ps === 'succeeded') return true;
  return false;
}

/** Deposit paywall: first name visible; last name masked (no full PII). */
function redactPersonNameForTeaser(fullName) {
  const s = String(fullName || '').trim();
  if (!s || /contact on file|unknown/i.test(s) || /^client$/i.test(s)) return 'Client *****';
  if (s.includes('@')) return 'Client *****';
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'Client *****';
  if (parts.length === 1) return `${parts[0]} *****`;
  const first = parts[0];
  const last = parts[parts.length - 1];
  const initial = last.length ? last.charAt(0).toUpperCase() : '*';
  return `${first} ${initial}*****`;
}

function preferredContactTeaserLabel(pc) {
  if (!pc) return null;
  if (pc === 'phone') return 'Phone';
  if (pc === 'text') return 'Text';
  if (pc === 'email') return 'Email';
  return String(pc).replace(/_/g, ' ');
}

function jobSummaryTeaser(description, category) {
  const d = String(description || '').replace(/\s+/g, ' ').trim();
  const cat = String(category || 'Service').trim();
  if (!d) return `${cat} — full scope unlocks after deposit`;
  const max = 92;
  const short = d.length > max ? `${d.slice(0, max)}…` : d;
  return short;
}

function postedMetaTeaser(createdAt) {
  if (!createdAt) return 'Posted recently';
  try {
    const d = typeof createdAt.toDate === 'function' ? createdAt.toDate() : new Date(createdAt);
    if (Number.isNaN(d.getTime())) return 'Posted recently';
    const now = Date.now();
    const diffMs = now - d.getTime();
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffDays <= 0) return 'Posted today';
    if (diffDays === 1) return 'Posted yesterday';
    if (diffDays < 7) return `Posted ${diffDays} days ago`;
    return `Posted ${d.toLocaleDateString()}`;
  } catch {
    return 'Posted recently';
  }
}

function priorityLabelForTeaser(priority) {
  const p = String(priority || 'normal').toLowerCase();
  if (p === 'urgent') return 'Urgent';
  if (p === 'high') return 'High priority';
  return 'Standard';
}

/** Up to 3 rows: real open-pool requests (redacted), padded with synthetic placeholders if needed. */
function buildDepositLockedPreviewRows(availableRequests, profData, resolvedTenantNames) {
  const cats = (profData?.categories || []).filter(Boolean);
  const fallbackCats = ['Plumbing', 'HVAC', 'Electrical', 'General Maintenance (Handyman)'];
  const pickCat = (i) => cats[i % (cats.length || 1)] || fallbackCats[i % fallbackCats.length];

  const syntheticTemplates = [
    { client: 'Jordan M*****', role: 'Client', jobLine: 'Leak under kitchen sink — water damage concern, needs same-week visit', pri: 'Urgent', meta: 'Posted today · in your service area' },
    { client: 'Sam R*****', role: 'Client', jobLine: 'AC not cooling upstairs — thermostat OK, possible recharge or capacitor', pri: 'High priority', meta: 'Posted 2 days ago · matches HVAC' },
    { client: 'Taylor K*****', role: 'Property owner', jobLine: 'Interior door sticking + minor drywall patch after move-out walkthrough', pri: 'Standard', meta: 'Posted this week · handyman fit' },
  ];

  const rows = [];
  availableRequests.slice(0, 3).forEach((req, i) => {
    const who = getMaintenanceRequesterDisplay(req, resolvedTenantNames);
    rows.push({
      key: req.id,
      cat: req.category || pickCat(i),
      client: redactPersonNameForTeaser(who.name),
      contactRole: who.roleLabel || 'Contact',
      jobLine: jobSummaryTeaser(req.description, req.category || pickCat(i)),
      priorityLabel: priorityLabelForTeaser(req.priority),
      meta: postedMetaTeaser(req.createdAt),
      roomHint: req.location ? String(req.location).replace(/_/g, ' ') : null,
      prefersLabel: preferredContactTeaserLabel(req.preferredContact),
      isReal: true,
    });
  });

  let s = 0;
  while (rows.length < 3) {
    const t = syntheticTemplates[s % syntheticTemplates.length];
    rows.push({
      key: `syn-${rows.length}`,
      cat: pickCat(rows.length),
      client: t.client,
      contactRole: t.role,
      jobLine: t.jobLine,
      priorityLabel: t.pri,
      meta: t.meta,
      roomHint: null,
      prefersLabel: null,
      isReal: false,
    });
    s += 1;
  }
  return rows;
}

function ProfessionalDashboard({ user, userData, onProfileUpdate, onLogout }) {
  const [activeTab, setActiveTab] = useState('setup'); // setup, overview, jobs, earnings, profile

  const [jobsFilter, setJobsFilter] = useState('available'); // 'available' | 'in_progress' | 'scheduled' | 'completed'
  const [profData, setProfData] = useState(userData);
  const [availableRequests, setAvailableRequests] = useState([]);
  const [acceptedRequests, setAcceptedRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [unitAddresses, setUnitAddresses] = useState({}); // Map of unitId to address
  const [resolvedTenantNames, setResolvedTenantNames] = useState({}); // tenantId -> display name from TenantsData

  // Earnings / paid pipeline (see listener below — split so "Mark complete" removes cards correctly)
  const [paidReadyJobs, setPaidReadyJobs] = useState([]); // Payment captured, not yet status completed
  const [completingPaidJobId, setCompletingPaidJobId] = useState(null);
  const [completedJobs, setCompletedJobs] = useState([]); // status === 'completed' && payment captured
  const [platformDefaultFeePct, setPlatformDefaultFeePct] = useState(null);
  const [markCompleteOkJobId, setMarkCompleteOkJobId] = useState(null);
  /** Paid & ready tab: completion note + optional photos before marking job complete (not the estimate step). */
  const [completionNoteByJobId, setCompletionNoteByJobId] = useState({});
  const [completionImagesByJobId, setCompletionImagesByJobId] = useState({});
  const [tenantReviewJobs, setTenantReviewJobs] = useState([]);
  /** Server-written job alerts (new pool jobs, status on linked requests). */
  const [proJobAlerts, setProJobAlerts] = useState([]);
  /** Per-request collapsible sections on job cards: `loc` | `visit` | `est` → false = collapsed. */
  const [proCardSectionsOpen, setProCardSectionsOpen] = useState({});

  const reviewStats = useMemo(() => {
    let sum = 0;
    let count = 0;
    let tenantCount = 0;
    let ownerCount = 0;
    tenantReviewJobs.forEach((j) => {
      const ts = normalizeRatingStars(j.tenantRating);
      if (ts != null) {
        sum += ts;
        count += 1;
        tenantCount += 1;
      }
      const os = normalizeRatingStars(j.ownerRating);
      if (os != null) {
        sum += os;
        count += 1;
        ownerCount += 1;
      }
    });
    return {
      count,
      avg: count > 0 ? sum / count : null,
      tenantCount,
      ownerCount,
    };
  }, [tenantReviewJobs]);

  // Setup form states
  const [businessName, setBusinessName] = useState(profData?.businessName || profData?.companyName || '');
  const [, setCategories] = useState(profData?.categories || []);
  const [serviceArea, setServiceArea] = useState(profData?.serviceArea || '');
  const [serviceRadius, setServiceRadius] = useState(profData?.serviceRadius || '20');
  const [serviceFee, setServiceFee] = useState(profData?.serviceFee || '');
  const [setupComplete, setSetupComplete] = useState(profData?.setupComplete || false);

  const [onboardingStep, setOnboardingStep] = useState(profData?.onboardingStep || 1);
  const [wizardTrades, setWizardTrades] = useState(
    () => profData?.selectedTrades?.length ? profData.selectedTrades : profData?.categories || []
  );
  const [qualHasLicense, setQualHasLicense] = useState(profData?.qualifications?.hasLicense ?? null);
  const [qualHasInsurance, setQualHasInsurance] = useState(profData?.qualifications?.hasInsurance ?? null);
  const [qualYearsExperience, setQualYearsExperience] = useState(profData?.qualifications?.yearsExperience || '');
  const [qualSpecialties, setQualSpecialties] = useState(profData?.qualifications?.specialties || '');
  const [qualHasReferences, setQualHasReferences] = useState(profData?.qualifications?.hasReferences ?? null);
  const [qualReferencesLink, setQualReferencesLink] = useState(profData?.qualifications?.referencesLink || '');
  const [wizardGated, setWizardGated] = useState(false);
  const [depositLoading, setDepositLoading] = useState(false);
  const [depositError, setDepositError] = useState('');
  const [depositPaid, setDepositPaid] = useState(profData?.depositPaid || false);
  const [wizardPhoneNumber, setWizardPhoneNumber] = useState(profData?.phoneNumber || userData?.phoneNumber || '');

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(PRO_LANDING_SIGNUP_NOTES_KEY);
      if (!raw) return;
      setQualSpecialties((prev) => (prev && String(prev).trim() ? prev : raw));
      sessionStorage.removeItem(PRO_LANDING_SIGNUP_NOTES_KEY);
    } catch (e) {
      console.warn('pro landing notes sessionStorage:', e);
    }
  }, []);

  // Profile editing states
  const [editingProfile, setEditingProfile] = useState(false);
  const [editEmail, setEditEmail] = useState(profData?.email || '');
  const [editBusinessName, setEditBusinessName] = useState(profData?.businessName || profData?.companyName || '');
  const [editCategories, setEditCategories] = useState(profData?.categories || []);
  const [editServiceArea, setEditServiceArea] = useState(profData?.serviceArea || '');
  const [editServiceRadius, setEditServiceRadius] = useState(profData?.serviceRadius || '20');
  const [editServiceFee, setEditServiceFee] = useState(profData?.serviceFee || '');
  const [editMessage, setEditMessage] = useState('');

  // Stripe Connect state
  const [stripeConnecting, setStripeConnecting] = useState(false);
  const [stripeConnectError, setStripeConnectError] = useState('');
  const [stripeConnectSuccess, setStripeConnectSuccess] = useState('');
  const [stripeLoginLoading, setStripeLoginLoading] = useState(false);
  /** Set when `window.open` for Stripe dashboard is blocked so the user can continue manually. */
  const [stripeDashboardOpenFallbackUrl, setStripeDashboardOpenFallbackUrl] = useState('');
  /** Inline error from Open Stripe Dashboard / login link (replaces blocking `alert`). */
  const [stripeLoginError, setStripeLoginError] = useState('');

  const [stripeEarningsLoading, setStripeEarningsLoading] = useState(false);
  const [stripeEarningsError, setStripeEarningsError] = useState('');
  const [stripeEarningsWarning, setStripeEarningsWarning] = useState('');

  // Meeting options state (per-request)
  const [showMeetingOptions, setShowMeetingOptions] = useState({});
  const [meetingDates, setMeetingDates] = useState({});
  const [meetingTimes, setMeetingTimes] = useState({});

  // Comment state
  const [commentText, setCommentText] = useState({});

  // Estimate state
  const [estimateAmount, setEstimateAmount] = useState({});
  const [estimateDescription, setEstimateDescription] = useState({});
  const [estimateImages, setEstimateImages] = useState({});
  const [estimateUploading, setEstimateUploading] = useState({});
  const [showEstimateForm, setShowEstimateForm] = useState({});
  const [editingEstimate, setEditingEstimate] = useState({});
  const [estimateSuccess, setEstimateSuccess] = useState({});

  // Load professional setup data
  useEffect(() => {
    if (!user) return;
    const profRef = doc(db, 'ProfessionalsData', user.uid);
    const unsubscribe = onSnapshot(profRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setProfData(data);
        setEditEmail(data.email || '');
        setBusinessName(data.businessName || data.companyName || '');
        setCategories(data.categories || []);
        setServiceArea(data.serviceArea || '');
        setServiceRadius(data.serviceRadius || '20');
        setServiceFee(data.serviceFee || '');
        setSetupComplete(data.setupComplete || false);
        setDepositPaid(data.depositPaid || false);
        if (data.onboardingStep && !data.setupComplete) {
          setOnboardingStep(data.onboardingStep);
        }
        if (data.selectedTrades?.length) {
          setWizardTrades(data.selectedTrades);
        } else if (data.categories?.length && !data.selectedTrades?.length) {
          setWizardTrades((prev) => (prev.length ? prev : data.categories));
        }
        if (data.qualifications) {
          const q = data.qualifications;
          if (q.hasLicense != null) setQualHasLicense(q.hasLicense);
          if (q.hasInsurance != null) setQualHasInsurance(q.hasInsurance);
          if (q.yearsExperience) setQualYearsExperience(q.yearsExperience);
          if (q.specialties) setQualSpecialties(q.specialties);
          if (q.hasReferences != null) setQualHasReferences(q.hasReferences);
          if (q.referencesLink) setQualReferencesLink(q.referencesLink);
        }
        if (data.phoneNumber) setWizardPhoneNumber(data.phoneNumber);

        // Auto-move to dashboard overview if setup complete
        if (data.setupComplete && activeTab === 'setup') {
          setActiveTab('overview');
          setJobsFilter('available');
        }
      }
    });
    return () => unsubscribe();
  }, [user, activeTab]);

  // Load available requests (filter by category match; handyman trade sees all categories)
  useEffect(() => {
    console.log('🔍 Available Requests Effect:', { setupComplete, profCategories: profData?.categories });
    if (!setupComplete || !profData?.categories) {
      console.log('⏭️ Skipping query - conditions not met');
      return;
    }
    
    // Query all requests (we'll filter on client-side)
    const q = query(
      collection(db, 'maintenance_requests'),
      orderBy('createdAt', 'desc')
    );
    
    const unsubscribe = onSnapshot(
      q, 
      (snap) => {
        console.log('📊 Total requests from DB:', snap.size);
        const requests = [];
        snap.forEach(doc => {
          const reqData = { id: doc.id, ...doc.data() };
          console.log('📋 Processing request:', { id: reqData.id, status: reqData.status, category: reqData.category, claimed: !!reqData.assignedProfessional });
          
          // Filter out invalid image URLs
          if (reqData.images && Array.isArray(reqData.images)) {
            reqData.images = reqData.images.filter(img => {
              try {
                return typeof img === 'string' && (img.startsWith('http://') || img.startsWith('https://') || img.startsWith('data:'));
              } catch (e) {
                console.warn('Invalid image URL detected:', img);
                return false;
              }
            });
          }
          
          // Show request if:
          // 1. Status is 'pending' or 'open'
          // 2. Not assigned exclusively to another professional
          // 3. Fewer than MAX professionals have proposed times (then the job leaves the open pool for everyone)
          // 4. This pro has not already claimed or submitted their own time proposal (those live under In Progress)
          // 5. Category matches (or handyman sees all), or request is "other"
          const assignedToOther = reqData.assignedProfessional && reqData.assignedProfessional.trim() !== '' && reqData.assignedProfessional !== user.uid;
          const iClaimed =
            Array.isArray(reqData.claimedByProfessionals) && reqData.claimedByProfessionals.includes(user.uid);
          const proposedByMe = !!getProposalForProfessional(reqData, user.uid);
          const proposalSlotsFull = countProposalProfessionals(reqData) >= MAX_PROFESSIONAL_MEETING_PROPOSALS;
          const statusOk = reqData.status === 'pending' || reqData.status === 'open';
          const categoryMatch = professionalSeesRequestCategory(profData.categories, reqData.category);

          const prefWindow = isPreferredOfferWindowActive(reqData);
          if (prefWindow && !proIsOnPreferredOfferSnapshot(reqData, user.uid)) {
            return;
          }

          const hideForThisPro = iClaimed || proposedByMe;
          if (statusOk && !assignedToOther && categoryMatch && !hideForThisPro && !proposalSlotsFull) {
            requests.push(reqData);
          }
        });
        requests.sort((a, b) => {
          const sb = availableRequestSortScore(b, user.uid);
          const sa = availableRequestSortScore(a, user.uid);
          if (sb !== sa) return sb - sa;
          const ta =
            a.createdAt?.toDate?.()?.getTime?.() ||
            (a.createdAt?.seconds ? a.createdAt.seconds * 1000 : 0) ||
            0;
          const tb =
            b.createdAt?.toDate?.()?.getTime?.() ||
            (b.createdAt?.seconds ? b.createdAt.seconds * 1000 : 0) ||
            0;
          return tb - ta;
        });
        console.log('✅ Filtered requests:', requests.length);
        setAvailableRequests(requests);
      },
      (error) => {
        console.error('Error loading available requests:', error);
        setAvailableRequests([]);
      }
    );
    return () => unsubscribe();
  }, [setupComplete, profData?.categories, user?.uid]);
  // Unit addresses for pool + claimed jobs (scheduled tab needs unit doc when tenantInfo.address is empty)
  useEffect(() => {
    const merged = [...availableRequests, ...acceptedRequests];
    const unitIds = [...new Set(merged.map((r) => r.unitId).filter(Boolean))];
    if (unitIds.length === 0) return;

    let cancelled = false;
    (async () => {
      const updates = {};
      for (const unitId of unitIds) {
        try {
          const unitSnap = await getDoc(doc(db, 'units', unitId));
          if (unitSnap.exists()) {
            updates[unitId] = unitSnap.data().address || '';
          }
        } catch (err) {
          console.error('Error fetching unit address for', unitId, err);
        }
      }
      if (!cancelled) {
        setUnitAddresses((prev) => ({ ...prev, ...updates }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [availableRequests, acceptedRequests]);

  // Resolve tenant display names when requests only store tenantId (legacy / minimal writes)
  useEffect(() => {
    let cancelled = false;
    const merged = [...availableRequests, ...acceptedRequests];
    const ids = [
      ...new Set(
        merged
          .filter(
            (r) =>
              r.tenantId &&
              !(r.tenantInfo?.fullName && String(r.tenantInfo.fullName).trim()) &&
              !(r.tenantName && String(r.tenantName).trim())
          )
          .map((r) => r.tenantId)
      ),
    ];
    if (ids.length === 0) return undefined;

    (async () => {
      const updates = {};
      for (const tid of ids) {
        try {
          const snap = await getDoc(doc(db, 'TenantsData', tid));
          if (!snap.exists()) continue;
          const d = snap.data();
          const nm =
            [d.firstName, d.lastName].filter(Boolean).join(' ').trim() ||
            (d.fullName && String(d.fullName).trim()) ||
            (d.name && String(d.name).trim()) ||
            (d.email && String(d.email).trim()) ||
            '';
          if (nm) updates[tid] = nm;
        } catch (e) {
          console.warn('Could not resolve tenant name', tid, e);
        }
      }
      if (!cancelled && Object.keys(updates).length > 0) {
        setResolvedTenantNames((prev) => {
          const next = { ...prev };
          for (const [k, v] of Object.entries(updates)) {
            if (!next[k]) next[k] = v;
          }
          return next;
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [availableRequests, acceptedRequests]);

  // Load claimed/assigned requests for this professional
  useEffect(() => {
    if (!setupComplete || !user) return;
    
    const q = query(
      collection(db, 'maintenance_requests'),
      orderBy('createdAt', 'desc')
    );
    
    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        const requests = [];
        snap.forEach(docSnap => {
          const reqData = { id: docSnap.id, ...docSnap.data() };
          
          // Filter out invalid image URLs
          if (reqData.images && Array.isArray(reqData.images)) {
            reqData.images = reqData.images.filter(img => {
              try {
                return typeof img === 'string' && (img.startsWith('http://') || img.startsWith('https://') || img.startsWith('data:'));
              } catch (e) {
                console.warn('Invalid image URL detected:', img);
                return false;
              }
            });
          }
          
          // Auto-fix: if acceptedMeetingTime exists but status isn't 'scheduled', fix it
          if (reqData.acceptedMeetingTime && reqData.status !== 'scheduled' && reqData.status !== 'completed') {
            console.log('🔧 Auto-fixing stuck request:', docSnap.id, 'from', reqData.status, 'to scheduled');
            updateDoc(doc(db, 'maintenance_requests', docSnap.id), { status: 'scheduled' }).catch(err => console.error('Auto-fix error:', err));
            reqData.status = 'scheduled'; // Update locally too
          }
          
          const isAssignedToMe = reqData.assignedProfessional === user.uid;
          const claimedByMe = Array.isArray(reqData.claimedByProfessionals) && reqData.claimedByProfessionals.includes(user.uid);
          if (isAssignedToMe || claimedByMe) {
            requests.push(reqData);
          }
        });
        setAcceptedRequests(requests);
      },
      (error) => {
        console.error('Error loading accepted requests:', error);
        setAcceptedRequests([]);
      }
    );
    return () => unsubscribe();
  }, [setupComplete, user]);

  useEffect(() => {
    if (!user?.uid) return;
    const feesRef = doc(db, 'PlatformSettings', 'fees');
    const unsub = onSnapshot(
      feesRef,
      (snap) => {
        const n = snap.data()?.platformFeePercentage;
        setPlatformDefaultFeePct(typeof n === 'number' && Number.isFinite(n) ? n : null);
      },
      () => setPlatformDefaultFeePct(null)
    );
    return () => unsub();
  }, [user?.uid]);

  // Load completed jobs for "completed" tab and earnings
  useEffect(() => {
    if (!setupComplete || !user) return;

    const q = query(
      collection(db, 'maintenance_requests'),
      where('assignedProfessional', '==', user.uid)
    );

    const unsubscribe = onSnapshot(q, (snap) => {
      const ready = [];
      const done = [];

      snap.forEach((d) => {
        const job = { id: d.id, ...d.data() };
        if (job.paymentStatus !== 'captured') return;
        const est = job.profesionalEstimate || job.professionalEstimate;
        if (!est || typeof est !== 'object') return;
        if (est.status && est.status !== 'accepted') return;
        if (job.status === 'completed') {
          done.push(job);
        } else if (job.status !== 'cancelled') {
          ready.push(job);
        }
      });

      setPaidReadyJobs(ready);
      setCompletedJobs(done);
    });
    return () => unsubscribe();
  }, [setupComplete, user]);

  const connectAccountId = useMemo(
    () => (profData?.stripeConnectId && String(profData.stripeConnectId).trim()) || '',
    [profData?.stripeConnectId]
  );

  const fetchStripeLifetimeEarnings = useCallback(async () => {
    if (!user?.uid || !connectAccountId) return;
    setStripeEarningsLoading(true);
    setStripeEarningsError('');
    setStripeEarningsWarning('');
    try {
      const fn = httpsCallable(firebaseFunctions, 'getStripeConnectLifetimeEarnings');
      const res = await fn({ professionalId: user.uid });
      const capped = Boolean(res?.data?.capped);
      setStripeEarningsWarning(
        capped
          ? 'Summary reached the transaction scan limit (25,000 lines). Contact support if amounts look incomplete.'
          : ''
      );
    } catch (e) {
      const msg =
        e?.message ||
        (typeof e?.details === 'string' ? e.details : '') ||
        'Could not load verified earnings from Stripe.';
      setStripeEarningsError(String(msg));
    } finally {
      setStripeEarningsLoading(false);
    }
  }, [user?.uid, connectAccountId]);

  useEffect(() => {
    if (activeTab !== 'earnings' || !setupComplete || !connectAccountId) return;
    fetchStripeLifetimeEarnings();
  }, [activeTab, setupComplete, connectAccountId, fetchStripeLifetimeEarnings]);

  // Reviews: load whenever signed in (not gated on setupComplete — otherwise pros on the setup screen never see ratings / Profile tab is hidden until setup).
  useEffect(() => {
    if (!user?.uid) return;

    const pushIfRated = (d, intoMap) => {
      const data = d.data();
      const hasTenant = normalizeRatingStars(data.tenantRating) != null;
      const hasOwner = normalizeRatingStars(data.ownerRating) != null;
      if (data.status === 'completed' && (hasTenant || hasOwner)) {
        intoMap.set(d.id, { id: d.id, ...data });
      }
    };

    const mergeAndSet = (snapAssigned, snapProposed) => {
      const byId = new Map();
      snapAssigned.forEach((d) => pushIfRated(d, byId));
      snapProposed.forEach((d) => pushIfRated(d, byId));
      const list = Array.from(byId.values());
      list.sort((a, b) => {
        const ta = a.completedAt?.toDate ? a.completedAt.toDate().getTime() : 0;
        const tb = b.completedAt?.toDate ? b.completedAt.toDate().getTime() : 0;
        return tb - ta;
      });
      setTenantReviewJobs(list);
    };

    let assignedDocs = [];
    let proposedDocs = [];
    const runMerge = () => {
      mergeAndSet(assignedDocs, proposedDocs);
    };

    const qAssigned = query(collection(db, 'maintenance_requests'), where('assignedProfessional', '==', user.uid));
    const qProposed = query(
      collection(db, 'maintenance_requests'),
      where('proposalProfessionalIds', 'array-contains', user.uid)
    );

    const unsubA = onSnapshot(
      qAssigned,
      (snap) => {
        assignedDocs = snap.docs;
        runMerge();
      },
      (err) => console.error('Error loading reviews (assigned):', err)
    );
    const unsubB = onSnapshot(
      qProposed,
      (snap) => {
        proposedDocs = snap.docs;
        runMerge();
      },
      (err) => console.error('Error loading reviews (proposed):', err)
    );
    return () => {
      unsubA();
      unsubB();
    };
  }, [user]);

  useEffect(() => {
    if (!user?.uid) return undefined;
    const qn = query(
      collection(db, 'notifications'),
      where('userId', '==', user.uid),
      orderBy('createdAt', 'desc'),
      limit(25)
    );
    const unsub = onSnapshot(
      qn,
      (snap) => {
        const rows = [];
        snap.forEach((d) => {
          const x = { id: d.id, ...d.data() };
          if (x.type === 'professional_pool_new_request' || x.type === 'professional_request_status') {
            rows.push(x);
          }
        });
        setProJobAlerts(rows);
      },
      (err) => console.error('Professional job alerts listener:', err)
    );
    return () => unsub();
  }, [user?.uid]);

  const markProJobAlertRead = useCallback(async (notificationId) => {
    try {
      await updateDoc(doc(db, 'notifications', notificationId), { read: true });
    } catch (e) {
      console.error('markProJobAlertRead:', e);
    }
  }, []);

  const markAllProJobAlertsRead = useCallback(async () => {
    const unread = proJobAlerts.filter((n) => !n.read);
    for (const n of unread) {
      await markProJobAlertRead(n.id);
    }
  }, [proJobAlerts, markProJobAlertRead]);

  const unreadProJobAlerts = useMemo(
    () => proJobAlerts.filter((n) => !n.read),
    [proJobAlerts]
  );

  const saveWizardStep = useCallback(
    async (stepNum, extraFields = {}) => {
      try {
        await updateDoc(doc(db, 'ProfessionalsData', user.uid), {
          onboardingStep: stepNum,
          updatedAt: serverTimestamp(),
          ...extraFields,
        });
      } catch (err) {
        console.error('saveWizardStep:', err);
      }
    },
    [user]
  );

  const handleWizardNext = useCallback(async () => {
    const step = onboardingStep;

    if (step === 1) {
      if (wizardTrades.length === 0) {
        alert('Please select at least one trade.');
        return;
      }
      await saveWizardStep(2, { selectedTrades: wizardTrades });
      setOnboardingStep(2);
    } else if (step === 2) {
      const licensedTrades = wizardTrades.filter((t) => TRADE_META[t]?.requiresLicense);
      if (licensedTrades.length > 0 && qualHasLicense !== true) {
        setWizardGated(true);
        return;
      }
      if (licensedTrades.length > 0 && qualHasInsurance !== true) {
        setWizardGated(true);
        return;
      }
      const isHandyman = wizardTrades.includes('General Maintenance (Handyman)');
      if (isHandyman && !qualYearsExperience) {
        alert('Please select your years of experience.');
        return;
      }

      await saveWizardStep(3, {
        qualifications: {
          hasLicense: qualHasLicense,
          hasInsurance: qualHasInsurance,
          yearsExperience: qualYearsExperience || null,
          specialties: qualSpecialties.trim() || null,
          hasReferences: qualHasReferences,
          referencesLink: qualReferencesLink.trim() || null,
        },
      });
      setOnboardingStep(3);
    } else if (step === 3) {
      if (!businessName.trim()) {
        alert('Please enter your business name.');
        return;
      }
      if (!serviceArea.trim()) {
        alert('Please enter your service area.');
        return;
      }
      if (!serviceFee) {
        alert('Please enter your service fee.');
        return;
      }
      const serviceAreaGeo = await geocodeAddressString(serviceArea.trim());
      await saveWizardStep(4, {
        businessName: businessName.trim(),
        categories: wizardTrades,
        serviceArea: serviceArea.trim(),
        serviceRadius: parseInt(serviceRadius, 10) || 20,
        serviceFee: parseFloat(serviceFee) || 0,
        phoneNumber: wizardPhoneNumber.trim() || null,
        ...(serviceAreaGeo ? { serviceAreaGeo } : {}),
      });
      setCategories(wizardTrades);
      setOnboardingStep(4);
    }
  }, [
    onboardingStep,
    wizardTrades,
    qualHasLicense,
    qualHasInsurance,
    qualYearsExperience,
    qualSpecialties,
    qualHasReferences,
    qualReferencesLink,
    businessName,
    serviceArea,
    serviceFee,
    serviceRadius,
    wizardPhoneNumber,
    saveWizardStep,
  ]);

  const handleWizardBack = useCallback(() => {
    setWizardGated(false);
    if (onboardingStep > 1) setOnboardingStep(onboardingStep - 1);
  }, [onboardingStep]);

  const handleDepositCheckout = useCallback(async () => {
    setDepositLoading(true);
    setDepositError('');
    try {
      const createCheckout = httpsCallable(firebaseFunctions, 'createProOnboardingCheckout');
      const result = await createCheckout({});
      const url = result.data?.url;
      if (url) {
        window.location.href = url;
      } else {
        setDepositError('Could not start checkout. Please try again.');
      }
    } catch (err) {
      console.error('Deposit checkout error:', err);
      setDepositError(
        err?.message ||
          'Could not start checkout. Please try again or contact hello@tenantdispatch.com.'
      );
    } finally {
      setDepositLoading(false);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('deposit_session_id');
    const cancelled = params.get('deposit_cancelled');
    if (!sessionId && !cancelled) return;

    const url = new URL(window.location.href);
    url.searchParams.delete('deposit_session_id');
    url.searchParams.delete('deposit_cancelled');
    window.history.replaceState({}, '', url.toString());

    if (cancelled) {
      setDepositError('Checkout was cancelled. You can try again when ready.');
      return;
    }

    if (sessionId && user) {
      (async () => {
        try {
          const verify = httpsCallable(firebaseFunctions, 'verifyProOnboardingDeposit');
          const result = await verify({ sessionId });
          if (result.data?.verified) {
            setDepositPaid(true);
            setOnboardingStep(5);
            await saveWizardStep(5, { depositPaid: true });
          } else {
            setDepositError('Payment could not be verified. Please contact hello@tenantdispatch.com.');
          }
        } catch (err) {
          console.error('Deposit verify error:', err);
          setDepositError(
            'Payment verification failed. If you were charged, contact hello@tenantdispatch.com.'
          );
        }
      })();
    }
  }, [user, saveWizardStep]);

  const handleFinalSetupComplete = useCallback(async () => {
    try {
      await updateDoc(doc(db, 'ProfessionalsData', user.uid), {
        setupComplete: true,
        onboardingStep: 5,
        updatedAt: serverTimestamp(),
      });
      setSetupComplete(true);
      setActiveTab('overview');
      setJobsFilter('available');
    } catch (error) {
      alert('Error completing setup: ' + error.message);
    }
  }, [user]);

  /**
   * Record this professional on the request (same Firestore field as before: `claimedByProfessionals`),
   * notify owner/tenant, then open **In progress** with the visit-time form so one tap replaces "claim + find scheduling".
   */
  const handleSuggestVisitTimes = async (req) => {
    const requestId = req.id;
    try {
      const requestRef = doc(db, 'maintenance_requests', requestId);
      await runTransaction(db, async (tx) => {
        const requestSnap = await tx.get(requestRef);
        if (!requestSnap.exists()) {
          throw new Error('Request not found');
        }
        const requestData = requestSnap.data() || {};
        const alreadyAssigned = requestData.assignedProfessional && String(requestData.assignedProfessional).trim() !== '';
        if (alreadyAssigned && requestData.assignedProfessional !== user.uid) {
          throw new Error('This request is already assigned to another professional.');
        }
        tx.update(requestRef, {
          claimedByProfessionals: arrayUnion(user.uid),
          professionalsInterestedAt: serverTimestamp()
        });
      });

      const requestSnap = await getDoc(requestRef);
      if (requestSnap.exists()) {
        const reqData = requestSnap.data();
        const notifyIds = [...new Set([reqData.tenantId, reqData.ownerId].filter(Boolean))];
        const cat = (reqData.category && String(reqData.category)) || 'maintenance';
        for (const userId of notifyIds) {
          await addDoc(collection(db, 'notifications'), {
            userId,
            type: 'professional_assigned',
            title: 'Professional interested',
            message: `${businessName} is interested in your ${cat} request and may suggest visit times.`,
            requestId: requestId,
            createdAt: serverTimestamp(),
            read: false
          });
        }
      }

      setActiveTab('jobs');
      setJobsFilter('in_progress');
      setSelectedRequest({ ...req, id: requestId });
      setShowMeetingOptions((prev) => ({ ...prev, [requestId]: true }));
    } catch (error) {
      alert('Could not start scheduling: ' + error.message);
    }
  };

  // Add comment to request discussion
  const handleAddComment = async (requestId, commentText) => {
    if (!commentText.trim()) return;
    
    try {
      await updateDoc(doc(db, 'maintenance_requests', requestId), {
        comments: arrayUnion({
          author: 'professional',
          text: commentText,
          createdAt: new Date().getTime()
        })
      });
      setCommentText(prev => ({ ...prev, [requestId]: '' }));
    } catch (error) {
      alert('Error adding comment: ' + error.message);
    }
  };

  // Propose meeting options (up to MAX_PROFESSIONAL_MEETING_PROPOSALS distinct professionals per request)
  const handleProposeMeetings = async (requestId) => {
    const dates = meetingDates[requestId] || ['', '', ''];
    const times = meetingTimes[requestId] || ['', '', ''];
    
    const options = dates.map((date, idx) => ({
      date,
      time: times[idx],
      accepted: false
    })).filter(opt => opt.date && opt.time);

    if (options.length === 0) {
      alert('Please provide at least one meeting time');
      return;
    }

    try {
      const requestRef = doc(db, 'maintenance_requests', requestId);
      await runTransaction(db, async (tx) => {
        const requestSnap = await tx.get(requestRef);
        if (!requestSnap.exists()) {
          throw new Error('Request not found');
        }
        const reqData = requestSnap.data() || {};
        const assignedToOther = reqData.assignedProfessional && reqData.assignedProfessional !== user.uid;
        if (assignedToOther) {
          throw new Error('This request is already assigned to another professional.');
        }

        let proposals = normalizeMeetingProposals({ ...reqData, id: requestSnap.id });
        const existingIdx = proposals.findIndex((p) => p.professionalId === user.uid);
        if (existingIdx === -1 && proposals.length >= MAX_PROFESSIONAL_MEETING_PROPOSALS) {
          throw new Error(
            `This request already has visit-time proposals from ${MAX_PROFESSIONAL_MEETING_PROPOSALS} professionals.`
          );
        }

        // Firestore forbids FieldValue.serverTimestamp() inside array elements; use client Timestamp.
        const entry = {
          professionalId: user.uid,
          businessName: businessName || 'Professional',
          proposedAt: Timestamp.now(),
          options,
        };
        const nextRaw = [...proposals];
        if (existingIdx >= 0) nextRaw[existingIdx] = entry;
        else nextRaw.push(entry);
        const next = sanitizeProfessionalMeetingProposalsForWrite(nextRaw);

        // No FieldValue.serverTimestamp() in the same update as a full array replace (Firestore rejects it).
        // claimedByProfessionals uses arrayUnion in a follow-up updateDoc.
        tx.update(requestRef, {
          professionalMeetingProposals: next,
          proposalProfessionalIds: next.map((p) => p.professionalId),
          meetingOptions: deleteField(),
          proposedByProfessional: deleteField(),
          meetingProposedAt: Timestamp.fromDate(new Date()),
        });
      });

      await updateDoc(requestRef, { claimedByProfessionals: arrayUnion(user.uid) });

      const requestSnapAfterUpdate = await getDoc(doc(db, 'maintenance_requests', requestId));
      if (requestSnapAfterUpdate.exists()) {
        const reqDataAfterUpdate = requestSnapAfterUpdate.data();
        const n = countProposalProfessionals(reqDataAfterUpdate);
        const notifyIds = [...new Set([reqDataAfterUpdate.tenantId, reqDataAfterUpdate.ownerId].filter(Boolean))];
        for (const uid of notifyIds) {
          await addDoc(collection(db, 'notifications'), {
            userId: uid,
            type: 'meeting_proposed',
            title: 'Meeting times proposed',
            message: `${businessName} suggested ${options.length} time(s). ${n} professional${n === 1 ? '' : 's'} have proposed — pick one in your request details.`,
            requestId: requestId,
            createdAt: serverTimestamp(),
            read: false
          });
        }
      }

      setShowMeetingOptions(prev => ({ ...prev, [requestId]: false }));
      setMeetingDates(prev => ({ ...prev, [requestId]: ['', '', ''] }));
      setMeetingTimes(prev => ({ ...prev, [requestId]: ['', '', ''] }));
    } catch (error) {
      alert('Error proposing meetings: ' + error.message);
    }
  };

  const handleSubmitEstimate = async (requestId) => {
    const amount = estimateAmount[requestId];
    const description = estimateDescription[requestId];
    const images = estimateImages[requestId] || [];
    const isEditing = editingEstimate[requestId];

    console.log('📝 handleSubmitEstimate called:', { requestId, amount, description, isEditing, imageCount: images.length });

    if (!amount || isNaN(amount) || parseFloat(amount) <= 0) {
      alert('Please enter a valid estimate amount');
      return;
    }

    try {
      setEstimateUploading(prev => ({ ...prev, [requestId]: true }));
      console.log('✅ Started estimate upload sequence');

      // Upload new images (filter out strings which are existing URLs)
      const newImageUrls = [];
      const existingImageUrls = [];
      
      if (images.length > 0) {
        for (const image of images) {
          if (typeof image === 'string') {
            // Already uploaded image URL
            existingImageUrls.push(image);
          } else {
            // New file to upload
            const imageRef = ref(storage, `estimate_images/${user.uid}/${Date.now()}_${image.name}`);
            await uploadBytes(imageRef, image);
            const url = await getDownloadURL(imageRef);
            newImageUrls.push(url);
          }
        }
      }
      
      // Combine existing and new images
      const allImageUrls = [...existingImageUrls, ...newImageUrls];

      // Get request data
      const requestSnap = await getDoc(doc(db, 'maintenance_requests', requestId));
      if (!requestSnap.exists()) {
        alert('Request not found');
        return;
      }

      const reqData = requestSnap.data();

      if (
        isEditing &&
        isProfessionalEstimateLocked({ ...reqData, id: requestId })
      ) {
        alert('This estimate was accepted or paid and can no longer be edited.');
        setEstimateUploading((prev) => ({ ...prev, [requestId]: false }));
        setEditingEstimate((prev) => ({ ...prev, [requestId]: false }));
        return;
      }
      
      // Update request with estimate
      // Build the update object - preserve status if editing, otherwise set to pending
      const estimateUpdate = {
        amount: parseFloat(amount),
        description: description || '',
        images: allImageUrls,
        submittedBy: {
          id: user.uid,
          businessName: profData?.businessName
        },
        submittedAt: isEditing ? reqData.profesionalEstimate?.submittedAt : serverTimestamp(),
      };

      // If editing, preserve existing status (accepted/rejected/pending)
      // If new submission, set to pending
      if (isEditing && reqData.profesionalEstimate?.status) {
        estimateUpdate.status = reqData.profesionalEstimate.status;
        estimateUpdate.updatedAt = serverTimestamp();
      } else {
        estimateUpdate.status = 'pending';
      }

      console.log('💾 Attempting to save estimateUpdate:', estimateUpdate);
      
      const updatePayload = { 'profesionalEstimate': estimateUpdate };
      console.log('📤 Update payload:', updatePayload);

      console.log('🔄 NOW CALLING updateDoc on request:', requestId);
      await updateDoc(doc(db, 'maintenance_requests', requestId), updatePayload);
      console.log('✅✅✅ SUCCESS! Estimate saved to Firestore for request:', requestId);

      // Immediately update local state so UI reflects change
      setAcceptedRequests(prev => 
        prev.map(req => 
          req.id === requestId 
            ? { ...req, profesionalEstimate: estimateUpdate }
            : req
        )
      );

      // Notify owner/tenant only on new submission
      const notificationUserId = reqData.tenantId || reqData.ownerId;
      if (notificationUserId && !isEditing) {
        await addDoc(collection(db, 'notifications'), {
          userId: notificationUserId,
          type: 'estimate_submitted',
          title: 'Estimate Submitted',
          message: `${profData?.businessName || 'Professional'} submitted an estimate of $${parseFloat(amount).toFixed(2)}`,
          requestId: requestId,
          createdAt: serverTimestamp(),
          read: false
        });
      }

      // Clear form
      setEstimateAmount(prev => ({ ...prev, [requestId]: '' }));
      setEstimateDescription(prev => ({ ...prev, [requestId]: '' }));
      setEstimateImages(prev => ({ ...prev, [requestId]: [] }));
      setShowEstimateForm(prev => ({ ...prev, [requestId]: false }));
      setEditingEstimate(prev => ({ ...prev, [requestId]: false }));

      // Show success message for 3 seconds
      const message = isEditing ? '✓ Estimate updated successfully!' : '✓ Estimate submitted successfully!';
      setEstimateSuccess(prev => ({ ...prev, [requestId]: message }));
      setTimeout(() => {
        setEstimateSuccess(prev => ({ ...prev, [requestId]: '' }));
      }, 3000);
    } catch (error) {
      console.error('❌❌❌ ERROR submitting estimate:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      alert('Error submitting estimate: ' + error.message);
    } finally {
      setEstimateUploading(prev => ({ ...prev, [requestId]: false }));
    }
  };

  const markPaidJobCompleteWithEvidence = useCallback(
    async (job) => {
      if (!user?.uid) return;
      const jobId = job.id;
      const note = String(completionNoteByJobId[jobId] || '').trim();
      if (note.length < PAID_JOB_COMPLETION_NOTE_MIN_LEN) {
        window.alert(
          `Describe what you completed on site (at least ${PAID_JOB_COMPLETION_NOTE_MIN_LEN} characters). You can add optional photos of the finished work.`
        );
        return;
      }
      if (note.length > PAID_JOB_COMPLETION_NOTE_MAX_LEN) {
        window.alert(`Completion notes are limited to ${PAID_JOB_COMPLETION_NOTE_MAX_LEN} characters.`);
        return;
      }
      const imgs = completionImagesByJobId[jobId] || [];
      if (imgs.length > PAID_JOB_COMPLETION_MAX_PHOTOS) {
        window.alert(`You can attach up to ${PAID_JOB_COMPLETION_MAX_PHOTOS} photos.`);
        return;
      }

      setCompletingPaidJobId(jobId);
      setMarkCompleteOkJobId(null);

      const imageUrls = [];
      try {
        for (const image of imgs) {
          if (typeof image === 'string') {
            if (image.startsWith('https://')) imageUrls.push(image);
          } else if (image && typeof image.name === 'string') {
            const safeName = String(image.name).replace(/[^a-zA-Z0-9._-]/g, '_');
            const imageRef = ref(storage, `job_completion_images/${user.uid}/${jobId}/${Date.now()}_${safeName}`);
            await uploadBytes(imageRef, image);
            imageUrls.push(await getDownloadURL(imageRef));
          }
        }

        const fn = httpsCallable(firebaseFunctions, 'professionalMarkMaintenanceJobComplete');
        await fn({
          maintenanceRequestId: jobId,
          completionSummary: note,
          completionImageUrls: imageUrls,
        });
        setCompletionNoteByJobId((prev) => ({ ...prev, [jobId]: '' }));
        setCompletionImagesByJobId((prev) => ({ ...prev, [jobId]: [] }));
        setMarkCompleteOkJobId(jobId);
        window.setTimeout(() => {
          setMarkCompleteOkJobId((prev) => (prev === jobId ? null : prev));
        }, 6000);
      } catch (err) {
        console.error('professionalMarkMaintenanceJobComplete', err);
        const code = typeof err?.code === 'string' ? err.code : '';
        const tryDirectUpdate =
          code === 'functions/not-found' ||
          code === 'functions/unavailable' ||
          code === 'functions/deadline-exceeded' ||
          code === 'functions/internal';
        if (tryDirectUpdate) {
          try {
            await updateDoc(doc(db, 'maintenance_requests', jobId), {
              status: 'completed',
              completedAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
              professionalCompletion: {
                summary: note,
                imageUrls,
                submittedAt: serverTimestamp(),
                submittedByProfessionalId: user.uid,
              },
            });
            setCompletionNoteByJobId((prev) => ({ ...prev, [jobId]: '' }));
            setCompletionImagesByJobId((prev) => ({ ...prev, [jobId]: [] }));
            setMarkCompleteOkJobId(jobId);
            window.setTimeout(() => {
              setMarkCompleteOkJobId((prev) => (prev === jobId ? null : prev));
            }, 6000);
          } catch (e2) {
            console.error('mark complete direct Firestore update', e2);
            const msg =
              err?.message ||
              err?.details ||
              code ||
              'Could not mark complete.';
            window.alert(`Could not mark complete: ${msg}`);
          }
        } else {
          const msg =
            err?.message ||
            err?.details ||
            code ||
            'Could not mark complete.';
          window.alert(`Could not mark complete: ${msg}`);
        }
      } finally {
        setCompletingPaidJobId(null);
      }
    },
    [user, completionNoteByJobId, completionImagesByJobId, firebaseFunctions]
  );

  // Save profile changes
  const handleSaveProfile = async () => {
    if (!editEmail.trim() || !editBusinessName.trim() || editCategories.length === 0 || !editServiceArea.trim() || !editServiceFee) {
      alert('Please fill in all fields');
      return;
    }

    try {
      const serviceAreaGeo = await geocodeAddressString(editServiceArea.trim());
      await updateDoc(doc(db, 'ProfessionalsData', user.uid), {
        email: editEmail.trim(),
        businessName: editBusinessName.trim(),
        categories: editCategories,
        serviceArea: editServiceArea.trim(),
        serviceRadius: parseInt(editServiceRadius),
        serviceFee: parseFloat(editServiceFee),
        updatedAt: serverTimestamp(),
        ...(serviceAreaGeo ? { serviceAreaGeo } : {}),
      });
      setEditMessage('✓ Profile updated successfully');
      setTimeout(() => setEditMessage(''), 3000);
      setEditingProfile(false);
      onProfileUpdate?.();
    } catch (error) {
      setEditMessage('❌ Error saving profile: ' + error.message);
    }
  };

  // Cancel profile editing
  const handleCancelEdit = () => {
    setEditEmail(profData?.email || '');
    setEditBusinessName(profData?.businessName || '');
    setEditCategories(profData?.categories || []);
    setEditServiceArea(profData?.serviceArea || '');
    setEditServiceRadius(profData?.serviceRadius || '20');
    setEditServiceFee(profData?.serviceFee || '');
    setEditMessage('');
    setEditingProfile(false);
  };

  // Initiate Stripe Connect onboarding
  const handleConnectStripe = async () => {
    setStripeConnecting(true);
    setStripeConnectError('');
    setStripeConnectSuccess('');
    setStripeDashboardOpenFallbackUrl('');
    setStripeLoginError('');

    try {
      const { httpsCallable } = await import('firebase/functions');
      const createOrLinkConnectAccount = httpsCallable(
        firebaseFunctions,
        'createOrLinkConnectAccount'
      );

      console.log('Calling createOrLinkConnectAccount for professionalId:', user.uid);
      const result = await createOrLinkConnectAccount({ professionalId: user.uid });
      
      if (result.data && result.data.accountLinkUrl) {
        const accountLinkUrl = result.data.accountLinkUrl;
        console.log('Navigating to Stripe onboarding URL:', accountLinkUrl);
        // Same-window navigation avoids popup blockers; Stripe `return_url` brings the user back.
        window.location.assign(accountLinkUrl);
        return;
      } else {
        throw new Error('No onboarding URL returned');
      }
    } catch (error) {
      console.error('Stripe Connect error:', error);
      
      // Check if it's a Connect setup error
      const msg = error.message || String(error);
      if (msg.includes('only create new accounts if')) {
        setStripeConnectError(
          '❌ Stripe Connect Not Enabled: The platform account needs to enable Stripe Connect first. ' +
          'Please contact the platform owner and ask them to set up Stripe Connect in their Stripe Dashboard. ' +
          'Learn more: https://stripe.com/docs/connect'
        );
      } else if (msg.includes('responsibilities') || msg.includes('platform-profile')) {
        setStripeConnectError(
          '❌ Error connecting Stripe: The platform owner must complete Connect setup in Stripe. ' +
          'Open this link in the Stripe Dashboard (logged in as the platform account): ' +
          'https://dashboard.stripe.com/settings/connect/platform-profile — then complete the "Responsibilities for managing losses" section and try again.'
        );
      } else {
        setStripeConnectError('❌ Error connecting Stripe: ' + msg);
      }
    } finally {
      setStripeConnecting(false);
    }
  };


  // Open Stripe Express dashboard via login link
  const handleOpenStripeDashboard = async () => {
    setStripeLoginLoading(true);
    setStripeDashboardOpenFallbackUrl('');
    setStripeLoginError('');
    try {
      const { httpsCallable } = await import('firebase/functions');
      const createStripeLoginLink = httpsCallable(firebaseFunctions, 'createStripeLoginLink');
      const result = await createStripeLoginLink({ professionalId: user.uid });
      if (result.data?.needsOnboarding && result.data?.accountLinkUrl) {
        window.location.assign(result.data.accountLinkUrl);
        return;
      }
      if (result.data?.url) {
        const url = result.data.url;
        const popup = window.open(url, '_blank', 'noopener,noreferrer');
        if (!popup) {
          setStripeDashboardOpenFallbackUrl(url);
        }
      }
    } catch (error) {
      console.error('Error opening Stripe dashboard:', error);
      const msg =
        error?.message ||
        error?.details ||
        (typeof error === 'string' ? error : 'Something went wrong.');
      setStripeLoginError(String(msg));
    } finally {
      setStripeLoginLoading(false);
    }
  };


  // Render Stripe Connect section
  const renderStripeConnectSection = (opts = {}) => {
    const embedded = Boolean(opts.embedded);
    const hasStripeAccountId = Boolean(profData?.stripeConnectId && String(profData.stripeConnectId).trim());
    const stripeStatusLower = String(profData?.stripeStatus || '').toLowerCase();
    const stripeExpressReady = stripeStatusLower === 'active';
    const stripeSetupInProgress =
      hasStripeAccountId &&
      (stripeStatusLower === 'onboarding' ||
        stripeStatusLower === 'pending' ||
        stripeStatusLower === 'disconnected' ||
        !stripeStatusLower);

    const statusBadge = hasStripeAccountId ? (
      stripeExpressReady ? (
        <div style={{ padding: '10px 16px', background: '#dcfce7', borderRadius: '6px', color: '#166534', fontSize: '13px', fontWeight: '600' }}>
          {'\u2713'} Connected
        </div>
      ) : (
        <div style={{ padding: '10px 16px', background: '#fef3c7', borderRadius: '6px', color: '#92400e', fontSize: '13px', fontWeight: '600' }}>
          Setup in progress
        </div>
      )
    ) : null;

    return (
      <div style={embedded ? { marginTop: 4 } : undefined}>
        {embedded ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              flexWrap: 'wrap',
              gap: 12,
              marginBottom: 18,
            }}
          >
            <div style={{ flex: '1 1 240px', minWidth: 0 }}>
              <h3 style={{ margin: '0 0 6px 0', fontSize: 18, fontWeight: 700, color: '#111827' }}>{'💳'} Bank & deposits</h3>
              <p style={{ color: '#64748b', margin: 0, fontSize: 14, lineHeight: 1.5 }}>
                Connect Stripe Express so homeowner payments can reach your bank. Use the dashboard for account and tax
                details.
              </p>
            </div>
            {statusBadge}
          </div>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
            <div>
              <h2 style={{ margin: '0 0 8px 0' }}>{'💳'} Bank & deposits</h2>
              <p style={{ color: '#666', margin: 0, fontSize: '14px', lineHeight: 1.45 }}>
                Connect Stripe Express so homeowner payments can reach your bank. Use the dashboard for account and tax
                details.
              </p>
            </div>
            {statusBadge}
          </div>
        )}
        {stripeConnectError && (
          <div style={{
            padding: '16px',
            borderRadius: '6px',
            marginBottom: '20px',
            background: '#fee2e2',
            color: '#991b1b',
            fontSize: '14px',
            border: '1px solid #fca5a5',
            lineHeight: '1.6'
          }}>
            <p style={{ margin: '0 0 8px 0', fontWeight: '600' }}>❌ {stripeConnectError.split(':')[0]}</p>
            <p style={{ margin: 0 }}>{stripeConnectError.includes('Contact the platform') ? stripeConnectError.split('Contact')[1] ? 'Contact ' + stripeConnectError.split('Contact')[1] : stripeConnectError : stripeConnectError}</p>
            {stripeConnectError.includes('Connect') && (
              <a href="https://stripe.com/docs/connect" target="_blank" rel="noopener noreferrer" 
                 style={{ color: '#dc2626', textDecoration: 'underline', fontWeight: '600', display: 'inline-block', marginTop: '8px' }}>
                Learn more about Stripe Connect →
              </a>
            )}
          </div>
        )}

        {stripeConnectSuccess && (
          <div style={{
            padding: '12px 16px',
            borderRadius: '6px',
            marginBottom: '20px',
            background: '#dcfce7',
            color: '#166534',
            fontSize: '14px',
            border: '1px solid #86efac'
          }}>
            {stripeConnectSuccess}
          </div>
        )}

        {stripeLoginError ? (
          <div
            style={{
              padding: '14px 16px',
              borderRadius: '8px',
              marginBottom: '16px',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              color: '#991b1b',
              fontSize: '14px',
              lineHeight: 1.5,
            }}
          >
            <p style={{ margin: '0 0 8px 0', fontWeight: 700 }}>Could not open Stripe Express</p>
            <p style={{ margin: 0 }}>{stripeLoginError}</p>
            <button
              type="button"
              onClick={() => setStripeLoginError('')}
              style={{
                marginTop: '10px',
                padding: '6px 12px',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
                background: 'transparent',
                border: '1px solid #f87171',
                borderRadius: '6px',
                color: '#b91c1c',
              }}
            >
              Dismiss
            </button>
          </div>
        ) : null}

        <div
          style={{
            background: '#fff',
            padding: '30px',
            borderRadius: '12px',
            border: '1px solid #e5e7eb',
            maxWidth: embedded ? 'none' : '600px',
          }}
        >
          {hasStripeAccountId ? (
            <div>
              {stripeExpressReady ? (
                embedded ? null : (
                <div style={{ marginBottom: '20px', padding: '16px', background: '#f0f9ff', borderRadius: '8px', borderLeft: '4px solid #0284c7' }}>
                  <p style={{ margin: 0, color: '#0c4a6e', fontSize: '14px' }}>
                    <strong>✓ Your Stripe account is connected and active.</strong>
                    <br />
                    You will receive payouts when homeowners complete job payments.
                  </p>
                </div>
                )
              ) : (
                <div style={{ marginBottom: '20px', padding: '16px', background: '#fffbeb', borderRadius: '8px', borderLeft: '4px solid #f59e0b' }}>
                  <p style={{ margin: 0, color: '#78350f', fontSize: '14px', lineHeight: 1.55 }}>
                    <strong>Finish Stripe Express setup.</strong> A connected account ID exists, but Stripe still needs onboarding
                    (business details, identity, or bank account) before payouts and the Express dashboard are available.
                    {stripeStatusLower === 'pending' ? (
                      <>
                        {' '}
                        <strong>Status:</strong> Stripe is reviewing your details — this can take a short time.
                      </>
                    ) : null}
                  </p>
                </div>
              )}
              {stripeSetupInProgress && !stripeExpressReady ? (
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleConnectStripe}
                  disabled={stripeConnecting}
                  style={{
                    width: '100%',
                    padding: '12px 20px',
                    fontSize: '15px',
                    fontWeight: '600',
                    marginBottom: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    opacity: stripeConnecting ? 0.6 : 1,
                    cursor: stripeConnecting ? 'not-allowed' : 'pointer',
                  }}
                >
                  {stripeConnecting ? '⏳ Loading…' : '↗ Continue Stripe setup'}
                </button>
              ) : null}
              <button
                className="btn-primary"
                onClick={handleOpenStripeDashboard}
                disabled={stripeLoginLoading || stripeConnecting}
                style={{
                  width: '100%',
                  padding: '12px 20px',
                  fontSize: '15px',
                  fontWeight: '600',
                  marginBottom: '20px',
                  background: '#635bff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  opacity: stripeLoginLoading || stripeConnecting ? 0.6 : 1,
                  cursor: stripeLoginLoading || stripeConnecting ? 'not-allowed' : 'pointer',
                }}
              >
                {stripeLoginLoading ? '⏳ Opening...' : '↗ Open Stripe Dashboard'}
              </button>
              {stripeDashboardOpenFallbackUrl ? (
                <div
                  style={{
                    marginBottom: '20px',
                    padding: '14px 16px',
                    borderRadius: '8px',
                    background: '#fef3c7',
                    border: '1px solid #fcd34d',
                    fontSize: '14px',
                    color: '#78350f',
                    lineHeight: 1.5,
                  }}
                >
                  <p style={{ margin: '0 0 10px 0', fontWeight: '600' }}>
                    Your browser blocked opening Stripe in a new tab.
                  </p>
                  <p style={{ margin: '0 0 12px 0' }}>
                    Use one of the options below to continue to your Stripe Express dashboard.
                  </p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
                    <a
                      href={stripeDashboardOpenFallbackUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'inline-block',
                        padding: '10px 16px',
                        background: '#635bff',
                        color: '#fff',
                        borderRadius: '6px',
                        fontWeight: '600',
                        textDecoration: 'none',
                      }}
                    >
                      Open Stripe (new tab)
                    </a>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => {
                        window.location.assign(stripeDashboardOpenFallbackUrl);
                      }}
                      style={{ padding: '10px 16px', fontWeight: '600', cursor: 'pointer' }}
                    >
                      Open in this window
                    </button>
                    <button
                      type="button"
                      onClick={() => setStripeDashboardOpenFallbackUrl('')}
                      style={{
                        padding: '10px 16px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        background: 'transparent',
                        border: '1px solid #d97706',
                        borderRadius: '6px',
                        color: '#92400e',
                      }}
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              ) : null}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
                <div>
                  <p style={{ color: '#999', fontSize: '12px', marginBottom: '4px', fontWeight: '600' }}>ACCOUNT ID</p>
                  <p style={{ fontSize: '13px', margin: 0, fontFamily: 'monospace', color: '#333', wordBreak: 'break-all' }}>{profData.stripeConnectId}</p>
                </div>
                <div>
                  <p style={{ color: '#999', fontSize: '12px', marginBottom: '4px', fontWeight: '600' }}>CONNECTED</p>
                  <p style={{ fontSize: '14px', margin: 0, color: '#166534', fontWeight: '500' }}>
                    {profData.stripeConnectCreatedAt ? new Date(profData.stripeConnectCreatedAt.toDate?.() || profData.stripeConnectCreatedAt).toLocaleDateString() : 'Recently'}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div>
              <div style={{ marginBottom: '24px', padding: '16px', background: '#fef3c7', borderRadius: '8px', borderLeft: '4px solid #f59e0b' }}>
                <p style={{ margin: 0, color: '#78350f', fontSize: '14px' }}>
                  <strong>⚠️ Connect your Stripe account to receive payments.</strong>
                  <br />This is required to accept payments from homeowners without keeping funds in the platform.
                </p>
              </div>
              
              <button
                className="btn-primary"
                onClick={handleConnectStripe}
                disabled={stripeConnecting}
                style={{
                  width: '100%',
                  padding: '14px 20px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: stripeConnecting ? 'not-allowed' : 'pointer',
                  opacity: stripeConnecting ? 0.6 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                {stripeConnecting ? (
                  <>
                    ⏳ Initializing...
                  </>
                ) : (
                  <>
                    🔗 Connect Stripe Account
                  </>
                )}
              </button>

              <p style={{ color: '#999', fontSize: '12px', marginTop: '16px', lineHeight: '1.5' }}>
                Click the button above to securely connect your Stripe Express account. This page will redirect to Stripe onboarding; when you finish, you will be returned here. No commitments required—you only get paid when jobs are completed.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Reject/remove a claimed request
  const handleRejectRequest = async (requestId) => {
    const confirmed = window.confirm('Are you sure you want to remove this request? It will return to available requests for other professionals.');
    if (!confirmed) return;

    try {
      const requestRef = doc(db, 'maintenance_requests', requestId);
      const requestSnap = await getDoc(requestRef);
      const reqData = requestSnap.exists() ? requestSnap.data() : {};
      const rawArr = Array.isArray(reqData.professionalMeetingProposals)
        ? reqData.professionalMeetingProposals
        : null;
      const hadInArray = rawArr?.some((p) => (p.professionalId || p.id) === user.uid);
      const hadLegacyProposal = reqData?.proposedByProfessional?.id === user.uid;
      const hadMyProposal = hadInArray || hadLegacyProposal;

      const updatePayload = {
        claimedByProfessionals: arrayRemove(user.uid),
      };
      if (reqData?.assignedProfessional === user.uid) updatePayload.assignedProfessional = null;
      if (hadMyProposal) {
        updatePayload.profesionalEstimate = null;
        if (rawArr && rawArr.length > 0) {
          const filtered = rawArr.filter((p) => (p.professionalId || p.id) !== user.uid);
          if (filtered.length === 0) {
            updatePayload.professionalMeetingProposals = deleteField();
            updatePayload.proposalProfessionalIds = deleteField();
          } else {
            updatePayload.professionalMeetingProposals = filtered;
            updatePayload.proposalProfessionalIds = filtered.map((p) => p.professionalId || p.id);
          }
          updatePayload.meetingOptions = deleteField();
          updatePayload.proposedByProfessional = deleteField();
        } else if (hadLegacyProposal) {
          updatePayload.meetingOptions = deleteField();
          updatePayload.proposedByProfessional = deleteField();
        }
      }

      await updateDoc(requestRef, updatePayload);

      // Optional: Add notification that professional rejected
      if (requestSnap.exists()) {
        const notificationUserId = reqData.tenantId || reqData.ownerId;
        if (notificationUserId) {
          await addDoc(collection(db, 'notifications'), {
            userId: notificationUserId,
            type: 'professional_rejected',
            title: 'Professional Removed Request',
            message: `${businessName} has removed themselves from the ${reqData.category} request`,
            requestId: requestId,
            createdAt: serverTimestamp(),
            read: false
          });
        }
      }

      setSelectedRequest(null);
    } catch (error) {
      console.error('Error rejecting request:', error);
      alert('Error removing request: ' + error.message);
    }
  };

  const inProgressRequests = useMemo(
    () =>
      acceptedRequests.filter(
        (r) => r.status !== 'scheduled' && r.status !== 'completed' && !r.acceptedMeetingTime
      ),
    [acceptedRequests]
  );

  const scheduledRequestsList = useMemo(
    () =>
      acceptedRequests.filter(
        (r) =>
          r.status !== 'completed' && (r.status === 'scheduled' || r.acceptedMeetingTime)
      ),
    [acceptedRequests]
  );

  const finishedWorkCount = useMemo(
    () => acceptedRequests.filter((r) => r.status === 'completed').length,
    [acceptedRequests]
  );

  const proPayoutFeePct = useMemo(
    () => effectiveProfessionalPlatformFeePercent(profData, platformDefaultFeePct),
    [profData, platformDefaultFeePct]
  );

  const totalNetEarned = useMemo(
    () =>
      completedJobs.reduce((sum, j) => sum + professionalNetPayoutDollars(j, proPayoutFeePct), 0),
    [completedJobs, proPayoutFeePct]
  );

  const stripeVerifiedLifetimeDollars = useMemo(() => {
    const c = profData?.stripeLifetimeNetCents;
    if (typeof c !== 'number' || !Number.isFinite(c)) return null;
    return Math.round(c) / 100;
  }, [profData?.stripeLifetimeNetCents]);

  const stripeEarningsSyncedLabel = useMemo(() => {
    const t = profData?.stripeEarningsSyncedAt;
    if (!t?.toDate) return null;
    try {
      return t.toDate().toLocaleString();
    } catch {
      return null;
    }
  }, [profData?.stripeEarningsSyncedAt]);

  const overviewNetEarningsDisplay = useMemo(() => {
    if (connectAccountId && stripeVerifiedLifetimeDollars != null) {
      return {
        dollars: stripeVerifiedLifetimeDollars,
        hint: `Verified net in Stripe · ${completedJobs.length} job${completedJobs.length !== 1 ? 's' : ''} completed in app · ${stripeEarningsSyncedLabel ? `last sync ${stripeEarningsSyncedLabel}` : 'open Getting paid to refresh'}`,
      };
    }
    return {
      dollars: totalNetEarned,
      hint: `From completed jobs in app (after platform fee) · ${completedJobs.length} completed · ${paidReadyJobs.length} paid, awaiting completion${connectAccountId ? ' · open Getting paid to sync Stripe' : ''}`,
    };
  }, [
    connectAccountId,
    stripeVerifiedLifetimeDollars,
    completedJobs.length,
    paidReadyJobs.length,
    totalNetEarned,
    stripeEarningsSyncedLabel,
  ]);

  const depositUnlocked = Boolean(depositPaid || profData?.adminLeadAccessGranted);

  const stripePayoutNeedsAttention = useMemo(() => {
    const id = profData?.stripeConnectId && String(profData.stripeConnectId).trim();
    if (!id) return true;
    const st = String(profData?.stripeStatus || '').toLowerCase();
    if (st === 'onboarding' || st === 'disconnected' || st === 'pending') return true;
    return false;
  }, [profData?.stripeConnectId, profData?.stripeStatus]);

  // Available requests section
  const renderAvailableRequests = (embedded = false) => (
    <div>
      {!embedded && (
        <>
          <h2 style={{ marginBottom: '10px' }}>📋 Available Service Requests</h2>
          <p style={{ color: '#666', marginBottom: '30px' }}>Requests matching your service categories</p>
        </>
      )}

      {/* Deposit gating: redacted previews only (no full job data until paid) */}
      {!depositPaid && (
        <>
          <div
            className="td-pro-deposit-banner"
            style={{
              position: 'relative',
              borderRadius: 14,
              border: '1px solid #e2e8f0',
              background: 'linear-gradient(180deg, #f0fdfa 0%, #ffffff 70%)',
              padding: 18,
              marginBottom: 14,
              overflow: 'hidden',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
              <div>
                <p style={{ margin: 0, fontWeight: 900, color: '#0f766e', fontSize: 15 }}>
                  🔒 Requests are hidden until your refundable deposit is complete
                </p>
                <p style={{ margin: '8px 0 0', color: '#475569', fontSize: 13, lineHeight: 1.5, maxWidth: 640 }}>
                  You have <strong>{Math.max(availableRequests.length, 3)} matching requests</strong> in your categories.
                  Until your deposit is complete, we show a <strong>limited, redacted preview</strong> (first name + masked last name,
                  brief issue summary, and timing). Full address, photos, phone numbers, and detailed notes are not shown.
                  <strong> Suggesting visit times on open jobs</strong> stays unavailable until the{' '}
                  <strong>$20 refundable deposit</strong> is complete.
                </p>
                {depositError ? (
                  <p style={{ margin: '10px 0 0', color: '#b91c1c', fontSize: 13 }}>{depositError}</p>
                ) : null}
              </div>
              <div style={{ minWidth: 240 }}>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleDepositCheckout}
                  disabled={depositLoading}
                  style={{ width: '100%' }}
                >
                  {depositLoading ? 'Redirecting…' : 'Pay $20 refundable deposit'}
                </button>
                <p style={{ margin: '8px 0 0', fontSize: 12, color: '#64748b' }}>
                  Deposit is refunded after your first completed paid job (or if not approved).
                </p>
              </div>
            </div>
          </div>

          <div className="requests-grid" aria-label="Redacted request previews">
            {buildDepositLockedPreviewRows(availableRequests, profData, resolvedTenantNames).map((p) => (
                <div
                  key={p.key}
                  className="request-card"
                  style={{
                    padding: '20px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '12px',
                    background: '#fff',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 10 }}>
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, textTransform: 'capitalize' }}>{p.cat}</h3>
                    <span
                      style={{
                        background: '#0f766e',
                        color: '#fff',
                        padding: '4px 10px',
                        borderRadius: 999,
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      Locked
                    </span>
                  </div>

                  <div style={{ marginBottom: 10 }}>
                    <span
                      style={{
                        padding: '3px 10px',
                        borderRadius: 999,
                        fontSize: 11,
                        fontWeight: 700,
                        background:
                          p.priorityLabel === 'Urgent'
                            ? '#ffebee'
                            : p.priorityLabel === 'High priority'
                              ? '#fff7ed'
                              : '#f1f5f9',
                        color:
                          p.priorityLabel === 'Urgent'
                            ? '#b91c1c'
                            : p.priorityLabel === 'High priority'
                              ? '#c2410c'
                              : '#475569',
                      }}
                    >
                      {p.priorityLabel === 'Urgent' ? '🚨 ' : p.priorityLabel === 'High priority' ? '⚡ ' : ''}
                      {p.priorityLabel}
                    </span>
                    {p.isReal ? (
                      <span
                        style={{
                          marginLeft: 8,
                          fontSize: 11,
                          fontWeight: 600,
                          color: '#059669',
                          background: '#ecfdf5',
                          padding: '3px 8px',
                          borderRadius: 999,
                        }}
                      >
                        Redacted listing
                      </span>
                    ) : null}
                  </div>

                  <div style={{ color: '#334155', fontSize: 13, lineHeight: 1.55 }}>
                    <p style={{ margin: 0, fontSize: 15, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em' }}>
                      {p.client}
                    </p>
                    <p style={{ margin: '4px 0 0', fontSize: 12, color: '#64748b' }}>
                      {p.contactRole}
                    </p>
                    <p style={{ margin: '10px 0 0', fontWeight: 600, color: '#1e293b' }}>{p.jobLine}</p>
                    {p.prefersLabel ? (
                      <p style={{ margin: '8px 0 0', color: '#64748b', fontSize: 12 }}>
                        Prefers <strong>{p.prefersLabel}</strong> · number/email hidden until deposit
                      </p>
                    ) : null}
                    {p.roomHint ? (
                      <p style={{ margin: '6px 0 0', color: '#64748b', fontSize: 12 }}>
                        📍 Room/area on file · <strong>hidden</strong> until deposit
                      </p>
                    ) : (
                      <p style={{ margin: '6px 0 0', color: '#94a3b8', fontSize: 12 }}>
                        Address and unit · hidden until deposit
                      </p>
                    )}
                    <p style={{ margin: '6px 0 0', color: '#64748b' }}>{p.meta}</p>
                    <p style={{ margin: '10px 0 0', fontSize: 12, color: '#b45309', fontWeight: 600 }}>
                      Suggest visit times on open jobs · after deposit only
                    </p>
                  </div>

                  <div
                    style={{
                      marginTop: 16,
                      paddingTop: 14,
                      borderTop: '1px dashed #cbd5e1',
                    }}
                  >
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={() => {
                        if (!depositLoading) handleDepositCheckout();
                      }}
                      disabled={depositLoading}
                      style={{
                        width: '100%',
                        padding: '12px 14px',
                        fontWeight: 700,
                        borderRadius: 10,
                        border: 'none',
                        cursor: depositLoading ? 'wait' : 'pointer',
                        background: '#0d9488',
                        color: '#fff',
                      }}
                    >
                      {depositLoading ? 'Opening checkout…' : 'Pay $20 deposit to unlock open jobs'}
                    </button>
                    <p style={{ margin: '10px 0 0', fontSize: 12, color: '#64748b', textAlign: 'center', lineHeight: 1.45 }}>
                      Full address, photos, and contacts unlock after your refundable deposit completes.
                    </p>
                  </div>
                </div>
              ))}

          </div>

          {/* Do not render real request cards until paid */}
          <div style={{ height: 12 }} />
          <div style={{ color: '#94a3b8', fontSize: 12, textAlign: 'center', marginTop: 8 }}>
            Pay the deposit to unlock the full board and suggest visit times on open jobs.
          </div>
          {availableRequests.length > 3 ? (
            <p style={{ textAlign: 'center', fontSize: 13, color: '#64748b', marginTop: 6 }}>
              + <strong>{availableRequests.length - 3}</strong> more open requests in your categories after unlock.
            </p>
          ) : null}
        </>
      )}

      {depositPaid && (availableRequests.length === 0 ? (
        <div style={{ background: '#f9fafb', padding: '40px', borderRadius: '12px', textAlign: 'center', border: '1px solid #e5e7eb' }}>
          <p style={{ color: '#666', fontSize: '15px' }}>No available requests at this time</p>
          <p style={{ color: '#999', fontSize: '13px' }}>Check back soon for service opportunities</p>
        </div>
      ) : (
        <div className="requests-grid">
          {availableRequests.map((req) => (
            <div
              key={req.id}
              className="request-card"
              style={{
                padding: '20px',
                border: '1px solid #e5e7eb',
                borderRadius: '12px',
                background: '#fff',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                filter: 'none',
                userSelect: 'auto',
                pointerEvents: 'auto',
              }}
            >
              {/* Header with Category and Badge */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', textTransform: 'capitalize' }}>{req.category}</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center', justifyContent: 'flex-end' }}>
                  {isPreferredOfferWindowActive(req) && proIsOnPreferredOfferSnapshot(req, user.uid) ? (
                    <span
                      title="Only preferred pros see this job until the window ends."
                      style={{
                        background: '#4f46e5',
                        color: '#fff',
                        padding: '4px 10px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: 700,
                      }}
                    >
                      Early access
                    </span>
                  ) : null}
                  {!isPreferredOfferWindowActive(req) && proIsOnPreferredOfferSnapshot(req, user.uid) ? (
                    <span
                      title="This owner had you on their preferred list for this job."
                      style={{
                        background: '#e0e7ff',
                        color: '#3730a3',
                        padding: '4px 10px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: 600,
                      }}
                    >
                      Your client
                    </span>
                  ) : null}
                  <span className="request-badge pending" style={{ background: '#f59e0b', color: '#fff', padding: '4px 10px', borderRadius: '4px', fontSize: '12px' }}>Available</span>
                </div>
              </div>

              {/* Priority Badge */}
              {req.priority && req.priority !== 'normal' && (
                <div style={{ marginBottom: '10px' }}>
                  <span style={{
                    padding: '4px 10px',
                    borderRadius: '12px',
                    fontSize: '11px',
                    fontWeight: '600',
                    background: req.priority === 'urgent' ? '#ffebee' : '#e8f5e9',
                    color: req.priority === 'urgent' ? '#c62828' : '#2e7d32',
                    textTransform: 'capitalize'
                  }}>
                    {req.priority === 'urgent' ? '🚨 Urgent' : '📋 Low Priority'}
                  </span>
                </div>
              )}

              {/* Property owner + client when both exist */}
              <div style={{ background: '#f9fafb', padding: '12px', borderRadius: '8px', margin: '12px 0', fontSize: '13px' }}>
                <ProMaintenanceContactRows
                  req={req}
                  resolvedTenantNames={resolvedTenantNames}
                  lineStyle={{ margin: '4px 0', fontSize: 13, color: '#334155', lineHeight: 1.5 }}
                  contactAfterAddress={false}
                />
                {req.preferredContact && (
                  <p style={{ margin: '8px 0 0 0', fontSize: 12, color: '#64748b' }}>
                    Prefers:{' '}
                    {req.preferredContact === 'phone'
                      ? 'Phone'
                      : req.preferredContact === 'text'
                        ? 'Text'
                        : req.preferredContact === 'email'
                          ? 'Email'
                          : req.preferredContact}
                  </p>
                )}
              </div>

              {/* Location Details */}
              <div style={{ background: '#e8f4f8', padding: '10px', borderRadius: '6px', margin: '12px 0', fontSize: '13px' }}>
                <p style={{ margin: '4px 0' }}>🏘️ <strong>Unit:</strong> {req.unitNumber}</p>
                {req.location && (
                  <p style={{ margin: '4px 0', textTransform: 'capitalize' }}>📍 <strong>Room:</strong> {req.location.replace('_', ' ')}</p>
                )}
                {unitAddresses[req.unitId] && (
                  <p style={{ margin: '4px 0', fontSize: '13px', lineHeight: 1.45 }}>
                    🏠{' '}
                    <a
                      href={buildProMapsHref(unitAddresses[req.unitId])}
                      className="td-pro-open-job-address-link"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => {
                        const href = buildProMapsHref(unitAddresses[req.unitId]);
                        if (href.startsWith('geo:')) {
                          e.preventDefault();
                          window.location.href = href;
                        }
                      }}
                    >
                      {unitAddresses[req.unitId]}
                    </a>
                  </p>
                )}
              </div>

              {/* Description */}
              <div style={{ margin: '12px 0', padding: '10px', background: '#f5f5f5', borderRadius: '6px', fontSize: '13px' }}>
                <p style={{ margin: '0 0 4px 0', fontWeight: '600', color: '#333' }}>Issue:</p>
                <p style={{ margin: '0', color: '#666' }}>{req.description}</p>
              </div>

              {/* Images */}
              {req.images && req.images.length > 0 && (
                <div style={{ margin: '12px 0' }}>
                  <p style={{ margin: '0 0 8px 0', fontSize: '12px', fontWeight: '600', color: '#333' }}>📸 Photos ({req.images.length})</p>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {req.images.map((url, idx) => (
                      <img
                        key={idx}
                        src={url}
                        alt={`Request attachment ${idx + 1}`}
                        style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #ddd', cursor: 'pointer' }}
                        onClick={() => window.open(url, '_blank')}
                        title="Click to view full size"
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Permission to Enter */}
              {req.permissionToEnter && (
                <div style={{ margin: '12px 0', padding: '8px', background: '#d4edda', borderRadius: '6px', fontSize: '12px', color: '#155724' }}>
                  ✓ <strong>Permission to enter if tenant not home</strong>
                </div>
              )}

              <button
                type="button"
                className="btn-primary td-pro-suggest-visit-btn"
                onClick={() => handleSuggestVisitTimes(req)}
                style={{ width: '100%', padding: '10px', marginTop: '12px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}
              >
                Suggest visit times
              </button>
              <p style={{ margin: '8px 0 0', fontSize: 12, color: '#64748b', textAlign: 'center', lineHeight: 1.4 }}>
                Adds you to this job and opens dates to propose for the owner or tenant.
              </p>
            </div>
          ))}
        </div>
      ))}
    </div>
  );

  // Accepted requests section
  const renderAcceptedRequests = (embedded = false) => (
    <div>
      {!embedded && (
        <>
          <h2 style={{ marginBottom: '10px' }}>⏳ Requests In Progress</h2>
          <p style={{ color: '#666', marginBottom: '30px' }}>Your active jobs</p>
        </>
      )}

      {inProgressRequests.length === 0 ? (
        <div style={{ background: '#f9fafb', padding: '40px', borderRadius: '12px', textAlign: 'center', border: '1px solid #e5e7eb' }}>
          <p style={{ color: '#666', fontSize: '15px' }}>No requests in progress</p>
          <p style={{ color: '#999', fontSize: '13px', lineHeight: 1.55, maxWidth: 440, margin: '0 auto' }}>
            Pick a job under <strong>Open jobs</strong>, tap <strong>Suggest visit times</strong>, or open an active card and
            use <strong>Schedule time now</strong>.
          </p>
        </div>
      ) : (
        <div className="requests-grid">
          {inProgressRequests.map((req) => {
            const address = req.tenantInfo?.address || unitAddresses[req.unitId] || '';
            const roomLabel = req.location ? String(req.location).replace(/_/g, ' ') : '';
            const statusLabel = String(req.status || 'in progress')
              .replace(/_/g, ' ')
              .replace(/\b\w/g, (c) => c.toUpperCase());
            const panelStyle = {
              marginTop: 14,
              padding: 14,
              background: '#f8fafc',
              borderRadius: 10,
              border: '1px solid #e2e8f0',
            };
            const labelStyle = {
              margin: '0 0 8px 0',
              fontSize: 11,
              fontWeight: 700,
              color: '#64748b',
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
            };
            const lineStyle = { margin: '6px 0', fontSize: 14, color: '#334155', lineHeight: 1.5 };

            const myMeetForCta = getProposalForProfessional(req, user.uid);
            const nPropForCta = countProposalProfessionals(req);
            const slotsBlockedForCta =
              nPropForCta >= MAX_PROFESSIONAL_MEETING_PROPOSALS && !myMeetForCta;

            return (
            <div 
              key={req.id} 
              className="request-card" 
              style={{
                padding: '18px', 
                border: '1px solid #e2e8f0', 
                borderRadius: '12px', 
                background: '#fff',
                boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onClick={() => setSelectedRequest(selectedRequest?.id === req.id ? null : req)}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 4 }}>
                <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#0f172a', textTransform: 'capitalize' }}>{req.category}</h3>
                <span
                  className={`request-badge ${proRequestStatusBadgeClass(req.status)}`}
                  style={{
                    flexShrink: 0,
                    padding: '4px 10px',
                    borderRadius: 6,
                    fontSize: 12,
                    fontWeight: 600,
                    textTransform: 'capitalize',
                  }}
                >
                  {statusLabel}
                </span>
              </div>

              {req.priority && req.priority !== 'normal' ? (
                <div style={{ marginTop: 8 }}>
                  <span
                    style={{
                      padding: '4px 10px',
                      borderRadius: 8,
                      fontSize: 11,
                      fontWeight: 600,
                      background: req.priority === 'urgent' ? '#fef2f2' : '#f1f5f9',
                      color: req.priority === 'urgent' ? '#b91c1c' : '#475569',
                      textTransform: 'capitalize',
                    }}
                  >
                    {req.priority === 'urgent' ? 'Urgent' : 'Low priority'}
                  </span>
                </div>
              ) : null}

              {req.description ? (
                <p style={{ margin: '10px 0 0 0', fontSize: 14, color: '#475569', lineHeight: 1.5 }}>{req.description}</p>
              ) : null}

              {req.aiTriageSummary ? (
                <div className="td-pro-ai-triage">
                  <p className="td-pro-ai-triage__label">AI triage summary</p>
                  <p className="td-pro-ai-triage__body">{req.aiTriageSummary}</p>
                </div>
              ) : null}

              <ProCollapsiblePanel
                role="group"
                ariaLabel="Location and contact"
                title="Location & contact"
                labelStyle={labelStyle}
                open={proCardSectionsOpen[req.id]?.loc !== false}
                onToggle={() => {
                  setProCardSectionsOpen((prev) => {
                    const cur = prev[req.id] || {};
                    const was = cur.loc !== false;
                    return { ...prev, [req.id]: { ...cur, loc: !was } };
                  });
                }}
                style={panelStyle}
                stopPropagation
              >
                <p style={{ ...lineStyle, marginTop: 0 }}>
                  <strong>Unit</strong> {req.unitNumber || '—'}
                  {roomLabel ? (
                    <>
                      {' '}
                      · <strong>Area</strong> {roomLabel}
                    </>
                  ) : null}
                </p>
                {address ? (
                  <p style={lineStyle}>
                    <a
                      href={`https://maps.google.com/?q=${encodeURIComponent(address)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#4f46e5', fontWeight: 500, textDecoration: 'none' }}
                    >
                      {address}
                    </a>
                  </p>
                ) : (
                  <p style={{ ...lineStyle, color: '#94a3b8' }}>Address not on file — confirm with the client or owner.</p>
                )}
                <ProMaintenanceContactRows
                  req={req}
                  resolvedTenantNames={resolvedTenantNames}
                  lineStyle={lineStyle}
                  contactAfterAddress
                />
                {req.preferredContact ? (
                  <p style={{ ...lineStyle, fontSize: 13, color: '#64748b' }}>
                    Prefers:{' '}
                    {req.preferredContact === 'phone'
                      ? 'Phone'
                      : req.preferredContact === 'text'
                        ? 'Text'
                        : req.preferredContact === 'email'
                          ? 'Email'
                          : req.preferredContact}
                  </p>
                ) : null}
                {req.permissionToEnter ? (
                  <p style={{ margin: '12px 0 0 0', fontSize: 12, color: '#475569' }}>
                    <strong>Access:</strong> Permission to enter if no one is home
                  </p>
                ) : null}
                {req.images && req.images.length > 0 ? (
                  <div style={{ marginTop: 12 }} onClick={(e) => e.stopPropagation()}>
                    <p
                      style={{
                        margin: '0 0 8px 0',
                        fontSize: 11,
                        fontWeight: 700,
                        color: '#64748b',
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                      }}
                    >
                      Photos ({req.images.length})
                    </p>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {req.images.map((url, idx) => (
                        <img
                          key={idx}
                          src={url}
                          alt=""
                          style={{
                            width: 56,
                            height: 56,
                            objectFit: 'cover',
                            borderRadius: 8,
                            border: '1px solid #e2e8f0',
                            cursor: 'pointer',
                          }}
                          onClick={() => window.open(url, '_blank')}
                        />
                      ))}
                    </div>
                  </div>
                ) : null}
              </ProCollapsiblePanel>

              <div className="td-pro-schedule-cta-row" onClick={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  className="btn-primary"
                  disabled={slotsBlockedForCta}
                  title={
                    slotsBlockedForCta
                      ? 'Three professionals have already proposed times for this request.'
                      : undefined
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedRequest(req);
                    if (!myMeetForCta && nPropForCta < MAX_PROFESSIONAL_MEETING_PROPOSALS) {
                      setShowMeetingOptions((prev) => ({ ...prev, [req.id]: true }));
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    fontSize: 15,
                    fontWeight: 700,
                    borderRadius: 10,
                    cursor: slotsBlockedForCta ? 'not-allowed' : 'pointer',
                    opacity: slotsBlockedForCta ? 0.65 : 1,
                  }}
                >
                  {slotsBlockedForCta
                    ? 'No open slots'
                    : myMeetForCta
                      ? 'View proposed times'
                      : 'Schedule time now'}
                </button>
                {!slotsBlockedForCta ? (
                  <p className="td-pro-schedule-cta-caption">
                    {myMeetForCta
                      ? 'Waiting for the owner or client to accept a time.'
                      : 'They confirm a visit from their dashboard after you send options.'}
                  </p>
                ) : (
                  <p className="td-pro-schedule-cta-caption td-pro-schedule-cta-caption--muted">
                    Other pros have filled the proposal slots for this job.
                  </p>
                )}
              </div>

              {selectedRequest?.id === req.id && (
                <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e5e7eb' }}>
                  {/* Comments/Discussion Section */}
                  <div style={{ marginBottom: '20px' }}>
                    <h4 style={{ margin: '0 0 12px 0' }}>💬 Discussion</h4>
                    <div style={{ background: '#f9fafb', padding: '12px', borderRadius: '6px', maxHeight: '250px', overflowY: 'auto', marginBottom: '12px' }}>
                      {!req.comments || req.comments.length === 0 ? (
                        <p style={{ color: '#999', fontSize: '13px', margin: 0 }}>No messages yet</p>
                      ) : (
                        req.comments.map((comment, idx) => (
                          <div key={idx} style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid #e5e7eb' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                              <strong style={{ fontSize: '13px' }}>{comment.author === 'professional' ? '🔧 You' : comment.author === 'owner' ? '🏠 Owner' : comment.author === 'tenant' ? '👤 Client' : '👤 User'}</strong>
                              <span style={{ fontSize: '11px', color: '#999' }}>
                                {comment.createdAt ? new Date(comment.createdAt).toLocaleString() : ''}
                              </span>
                            </div>
                            <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#333' }}>{comment.text}</p>
                          </div>
                        ))
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                      <textarea
                        placeholder="Add a question or comment..."
                        value={commentText[req.id] || ''}
                        onChange={(e) => setCommentText(prev => ({ ...prev, [req.id]: e.target.value }))}
                        onClick={(e) => e.stopPropagation()}
                        style={{ flex: 1, padding: '8px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', fontFamily: 'inherit', minHeight: '60px' }}
                      />
                    </div>
                    <button 
                      className="btn-small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddComment(req.id, commentText[req.id] || '');
                      }}
                      style={{ background: '#1976d2', color: '#fff', padding: '8px 16px', borderRadius: '4px', border: 'none', cursor: 'pointer' }}
                    >
                      Send Message
                    </button>
                  </div>

                  {/* Meeting Options Section */}
                  {(() => {
                    const myMeet = getProposalForProfessional(req, user.uid);
                    const nProp = countProposalProfessionals(req);
                    return (
                      <>
                  {!myMeet && !showMeetingOptions[req.id] && nProp < MAX_PROFESSIONAL_MEETING_PROPOSALS && (
                    <button 
                      className="btn-primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowMeetingOptions(prev => ({ ...prev, [req.id]: true }));
                      }}
                      style={{ width: '100%', padding: '10px', marginTop: '12px' }}
                    >
                      📅 Propose meeting times
                    </button>
                  )}

                  {showMeetingOptions[req.id] && (
                    <div style={{ marginTop: '12px', padding: '12px', background: '#f0f9ff', borderRadius: '6px', borderLeft: '4px solid #0284c7' }}>
                      <h4 style={{ margin: '0 0 8px 0', fontSize: '14px' }}>📅 Propose Up to 3 Meeting Times</h4>
                      <p style={{ margin: '0 0 12px 0', fontSize: 12, color: '#64748b', lineHeight: 1.45 }}>
                        When this request has both a tenant and a property owner on file, they each get a notification and can accept a time from their own dashboard. First acceptance schedules the visit.
                      </p>
                      {[0, 1, 2].map(idx => (
                        <div key={idx} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                          <input
                            type="date"
                            value={(meetingDates[req.id] || ['', '', ''])[idx]}
                            onChange={(e) => {
                              const newDates = [...(meetingDates[req.id] || ['', '', ''])];
                              newDates[idx] = e.target.value;
                              setMeetingDates(prev => ({ ...prev, [req.id]: newDates }));
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="input-field"
                            style={{ flex: 1, padding: '8px' }}
                          />
                          <input
                            type="time"
                            value={(meetingTimes[req.id] || ['', '', ''])[idx]}
                            onChange={(e) => {
                              const newTimes = [...(meetingTimes[req.id] || ['', '', ''])];
                              newTimes[idx] = e.target.value;
                              setMeetingTimes(prev => ({ ...prev, [req.id]: newTimes }));
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="input-field"
                            style={{ flex: 1, padding: '8px' }}
                          />
                        </div>
                      ))}
                      <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                        <button 
                          className="btn-primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleProposeMeetings(req.id);
                          }}
                          style={{ flex: 1, padding: '8px' }}
                        >
                          Submit
                        </button>
                        <button 
                          className="btn-small cancel"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowMeetingOptions(prev => ({ ...prev, [req.id]: false }));
                          }}
                          style={{ flex: 1 }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {myMeet && myMeet.options?.length > 0 ? (
                    <div style={{ marginTop: '12px', padding: '12px', background: '#f0fdf4', borderRadius: '6px', borderLeft: '4px solid #10b981' }}>
                      <h4 style={{ margin: '0 0 12px 0', fontSize: '14px' }}>📅 Your proposed times</h4>
                      {myMeet.options.map((option, idx) => (
                        <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px', background: '#fff', borderRadius: '4px', marginBottom: '8px', border: '1px solid #e5e7eb' }}>
                          <span style={{ fontSize: '13px' }}>{formatMeetingOptionForViewer(option.date, option.time)}</span>
                          <span style={{ fontSize: '12px', color: '#10b981', fontWeight: '600' }}>
                            {option.accepted ? '✓ Accepted' : '⏳ Pending client/owner'}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : null}
                      </>
                    );
                  })()}

                  {/* Actions Section */}
                  <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e5e7eb', display: 'flex', gap: '8px' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRejectRequest(req.id);
                      }}
                      style={{
                        flex: 1,
                        padding: '10px',
                        background: '#fee2e2',
                        color: '#991b1b',
                        border: '1px solid #fecaca',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '14px'
                      }}
                    >
                      ❌ Remove Request
                    </button>
                  </div>
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const renderScheduledRequests = (embedded = false) => (
    <div>
      {!embedded && (
        <>
          <h2 style={{ marginBottom: '10px' }}>📅 Scheduled Appointments</h2>
          <p style={{ color: '#666', marginBottom: '30px' }}>Your confirmed service appointments</p>
        </>
      )}

      {scheduledRequestsList.length === 0 ? (
        <div style={{ background: '#f9fafb', padding: '40px', borderRadius: '12px', textAlign: 'center', border: '1px solid #e5e7eb' }}>
          <p style={{ color: '#666', fontSize: '15px' }}>No scheduled appointments yet</p>
          <p style={{ color: '#999', fontSize: '13px' }}>Once a tenant or owner accepts your proposed time, it will appear here</p>
        </div>
      ) : (
        <div className="requests-grid">
          {scheduledRequestsList.map((req) => {
              const address = req.tenantInfo?.address || unitAddresses[req.unitId] || '';
              const acceptedWhen = req.acceptedMeetingTime?.acceptedAt
                ? new Date(
                    req.acceptedMeetingTime.acceptedAt.seconds
                      ? req.acceptedMeetingTime.acceptedAt.seconds * 1000
                      : req.acceptedMeetingTime.acceptedAt
                  ).toLocaleString()
                : '';
              const estimateLocked = isProfessionalEstimateLocked(req);
              const viewingEstimateEdit = Boolean(editingEstimate[req.id] && !estimateLocked);
              const statusLabel =
                req.status === 'scheduled'
                  ? 'Scheduled'
                  : String(req.status || 'Active')
                      .replace(/_/g, ' ')
                      .replace(/\b\w/g, (c) => c.toUpperCase());
              const roomLabel = req.location ? String(req.location).replace(/_/g, ' ') : '';

              const panelStyle = {
                marginTop: 14,
                padding: 14,
                background: '#f8fafc',
                borderRadius: 10,
                border: '1px solid #e2e8f0',
              };
              const labelStyle = {
                margin: '0 0 8px 0',
                fontSize: 11,
                fontWeight: 700,
                color: '#64748b',
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
              };
              const lineStyle = { margin: '6px 0', fontSize: 14, color: '#334155', lineHeight: 1.5 };
              const estimatePanelTitle = viewingEstimateEdit
                ? 'Edit estimate'
                : req.profesionalEstimate
                  ? 'Estimate'
                  : showEstimateForm[req.id]
                    ? 'New estimate'
                    : 'Estimate';
              const estimatePanelSurface =
                viewingEstimateEdit || (!req.profesionalEstimate && showEstimateForm[req.id])
                  ? { ...panelStyle, marginTop: 14, border: '1px solid #cbd5e1' }
                  : req.profesionalEstimate
                    ? { ...panelStyle, marginTop: 14, background: '#fafafa' }
                    : { ...panelStyle, marginTop: 14 };

              return (
                <div
                  key={req.id}
                  className="request-card"
                  style={{
                    padding: 18,
                    border: '1px solid #e2e8f0',
                    borderRadius: 12,
                    background: '#fff',
                    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 4 }}>
                    <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#0f172a', textTransform: 'capitalize' }}>
                      {req.category}
                    </h3>
                    <span
                      className={`request-badge ${proRequestStatusBadgeClass(req.status)}`}
                      style={{
                        flexShrink: 0,
                        padding: '4px 10px',
                        borderRadius: 6,
                        fontSize: 12,
                        fontWeight: 600,
                        textTransform: 'capitalize',
                      }}
                    >
                      {statusLabel}
                    </span>
                  </div>
                  {req.description ? (
                    <p style={{ margin: '8px 0 0 0', fontSize: 14, color: '#475569', lineHeight: 1.5 }}>{req.description}</p>
                  ) : null}

                  <ProCollapsiblePanel
                    title="Location & contact"
                    labelStyle={labelStyle}
                    open={proCardSectionsOpen[req.id]?.loc !== false}
                    onToggle={() => {
                      setProCardSectionsOpen((prev) => {
                        const cur = prev[req.id] || {};
                        const was = cur.loc !== false;
                        return { ...prev, [req.id]: { ...cur, loc: !was } };
                      });
                    }}
                    style={{ ...panelStyle, marginTop: 14 }}
                  >
                    <p style={{ ...lineStyle, marginTop: 0 }}>
                      <strong>Unit</strong> {req.unitNumber || '—'}
                      {roomLabel ? (
                        <>
                          {' '}
                          · <strong>Area</strong> {roomLabel}
                        </>
                      ) : null}
                    </p>
                    {address ? (
                      <p style={lineStyle}>
                        <a
                          href={`https://maps.google.com/?q=${encodeURIComponent(address)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#4f46e5', fontWeight: 500, textDecoration: 'none' }}
                        >
                          {address}
                        </a>
                      </p>
                    ) : (
                      <p style={{ ...lineStyle, color: '#94a3b8' }}>Address not on file — use unit details above.</p>
                    )}
                    <ProMaintenanceContactRows
                      req={req}
                      resolvedTenantNames={resolvedTenantNames}
                      lineStyle={lineStyle}
                      contactAfterAddress
                    />
                  </ProCollapsiblePanel>

                  {req.acceptedMeetingTime ? (
                    <ProCollapsiblePanel
                      title="Confirmed visit"
                      labelStyle={labelStyle}
                      open={proCardSectionsOpen[req.id]?.visit !== false}
                      onToggle={() => {
                        setProCardSectionsOpen((prev) => {
                          const cur = prev[req.id] || {};
                          const was = cur.visit !== false;
                          return { ...prev, [req.id]: { ...cur, visit: !was } };
                        });
                      }}
                      style={{ ...panelStyle, marginTop: 12, background: '#fafafa' }}
                    >
                      <p style={{ ...lineStyle, marginTop: 0, fontWeight: 600, color: '#0f172a' }}>
                        {formatMeetingOptionForViewer(req.acceptedMeetingTime.date, req.acceptedMeetingTime.time)}
                      </p>
                      {acceptedWhen ? (
                        <p style={{ margin: '8px 0 0 0', fontSize: 12, color: '#94a3b8' }}>
                          Confirmed {acceptedWhen}
                          {req.acceptedMeetingTime.acceptedBy === 'tenant'
                            ? ' · Client'
                            : req.acceptedMeetingTime.acceptedBy === 'owner'
                              ? ' · Owner'
                              : ''}
                        </p>
                      ) : null}
                    </ProCollapsiblePanel>
                  ) : null}

                  {!req.profesionalEstimate && estimateSuccess[req.id] ? (
                    <div
                      style={{
                        marginTop: 12,
                        padding: '12px',
                        background: '#f1f5f9',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        color: '#334155',
                        fontSize: '13px',
                        fontWeight: '600',
                        textAlign: 'center',
                      }}
                    >
                      {estimateSuccess[req.id]}
                    </div>
                  ) : null}

                  <ProCollapsiblePanel
                    title={estimatePanelTitle}
                    labelStyle={labelStyle}
                    open={proCardSectionsOpen[req.id]?.est !== false}
                    onToggle={() => {
                      setProCardSectionsOpen((prev) => {
                        const cur = prev[req.id] || {};
                        const was = cur.est !== false;
                        return { ...prev, [req.id]: { ...cur, est: !was } };
                      });
                    }}
                    style={estimatePanelSurface}
                  >
                  {req.profesionalEstimate ? (
                    viewingEstimateEdit ? (
                      <>
                        <h4 style={{ margin: '0 0 12px 0', fontSize: 15, fontWeight: 700, color: '#0f172a' }}>Update amount & details</h4>
                        
                        {/* Estimate Amount */}
                        <div style={{ marginBottom: '12px' }}>
                          <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', fontSize: '13px' }}>
                            Total Estimate Amount <span style={{ color: '#dc3545' }}>*</span>
                          </label>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ fontSize: '16px', fontWeight: '600' }}>$</span>
                            <input
                              type="number"
                              step="0.01"
                              value={estimateAmount[req.id] || ''}
                              onChange={(e) => setEstimateAmount(prev => ({ ...prev, [req.id]: e.target.value }))}
                              placeholder="0.00"
                              style={{
                                flex: 1,
                                padding: '8px',
                                border: '1px solid #ddd',
                                borderRadius: '6px',
                                fontSize: '14px',
                                fontFamily: 'inherit'
                              }}
                            />
                          </div>
                        </div>

                        {/* Description */}
                        <div style={{ marginBottom: '12px' }}>
                          <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', fontSize: '13px' }}>
                            Description (optional)
                          </label>
                          <textarea
                            value={estimateDescription[req.id] || ''}
                            onChange={(e) => setEstimateDescription(prev => ({ ...prev, [req.id]: e.target.value }))}
                            placeholder="Describe the work to be done..."
                            style={{
                              width: '100%',
                              padding: '8px',
                              border: '1px solid #ddd',
                              borderRadius: '6px',
                              fontSize: '13px',
                              fontFamily: 'inherit',
                              minHeight: '60px',
                              resize: 'vertical'
                            }}
                          />
                        </div>

                        {/* Images */}
                        <div style={{ marginBottom: '12px' }}>
                          <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', fontSize: '13px' }}>
                            Photos (optional)
                          </label>
                          <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                            <button
                              onClick={() => {
                                const input = document.createElement('input');
                                input.type = 'file';
                                input.accept = 'image/*';
                                input.capture = 'environment';
                                input.onChange = (e) => {
                                  const files = Array.from(e.target.files || []);
                                  setEstimateImages(prev => ({
                                    ...prev,
                                    [req.id]: [...(prev[req.id] || []), ...files]
                                  }));
                                };
                                input.click();
                              }}
                              style={{
                                flex: 1,
                                padding: '10px',
                                background: '#fff',
                                color: '#334155',
                                border: '1px solid #cbd5e1',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '13px',
                                fontWeight: '600'
                              }}
                            >
                              Take photo
                            </button>
                            <button
                              onClick={() => {
                                const input = document.createElement('input');
                                input.type = 'file';
                                input.accept = 'image/*';
                                input.multiple = true;
                                input.onChange = (e) => {
                                  const files = Array.from(e.target.files || []);
                                  setEstimateImages(prev => ({
                                    ...prev,
                                    [req.id]: [...(prev[req.id] || []), ...files]
                                  }));
                                };
                                input.click();
                              }}
                              style={{
                                flex: 1,
                                padding: '10px',
                                background: '#fff',
                                color: '#334155',
                                border: '1px solid #cbd5e1',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '13px',
                                fontWeight: '600'
                              }}
                            >
                              Add photos
                            </button>
                          </div>
                          {estimateImages[req.id] && estimateImages[req.id].length > 0 && (
                            <div style={{ marginTop: '8px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                              {estimateImages[req.id].map((img, idx) => (
                                <div key={idx} style={{ position: 'relative' }}>
                                  <img
                                    src={typeof img === 'string' ? img : URL.createObjectURL(img)}
                                    alt={`Upload ${idx + 1}`}
                                    style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                                  />
                                  <button
                                    onClick={() => {
                                      setEstimateImages(prev => ({
                                        ...prev,
                                        [req.id]: prev[req.id].filter((_, i) => i !== idx)
                                      }));
                                    }}
                                    style={{
                                      position: 'absolute',
                                      top: '-8px',
                                      right: '-8px',
                                      width: '20px',
                                      height: '20px',
                                      borderRadius: '50%',
                                      border: 'none',
                                      background: '#64748b',
                                      color: '#fff',
                                      cursor: 'pointer',
                                      fontSize: '12px',
                                      fontWeight: 'bold'
                                    }}
                                  >×</button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Buttons */}
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => handleSubmitEstimate(req.id)}
                            disabled={estimateUploading[req.id]}
                            style={{
                              flex: 1,
                              padding: '10px',
                              background: '#334155',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '8px',
                              cursor: estimateUploading[req.id] ? 'not-allowed' : 'pointer',
                              fontSize: '13px',
                              fontWeight: '600',
                              opacity: estimateUploading[req.id] ? 0.7 : 1,
                            }}
                          >
                            {estimateUploading[req.id] ? 'Updating...' : 'Save estimate'}
                          </button>
                          <button
                            onClick={() => {
                              setEditingEstimate(prev => ({ ...prev, [req.id]: false }));
                              setEstimateAmount(prev => ({ ...prev, [req.id]: '' }));
                              setEstimateDescription(prev => ({ ...prev, [req.id]: '' }));
                              setEstimateImages(prev => ({ ...prev, [req.id]: [] }));
                            }}
                            style={{
                              flex: 1,
                              padding: '10px',
                              background: '#fff',
                              color: '#334155',
                              border: '1px solid #cbd5e1',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontSize: '13px',
                              fontWeight: '600'
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <p style={{ ...lineStyle, marginTop: 0, fontSize: 18, fontWeight: 700, color: '#0f172a' }}>
                          ${parseFloat(req.profesionalEstimate.amount).toFixed(2)}
                        </p>
                        {req.profesionalEstimate.description ? (
                          <p style={{ ...lineStyle, fontSize: 13 }}>{req.profesionalEstimate.description}</p>
                        ) : null}
                        {req.profesionalEstimate.images && req.profesionalEstimate.images.length > 0 ? (
                          <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {req.profesionalEstimate.images.map((url, idx) => (
                              <img
                                key={idx}
                                src={url}
                                alt={`Estimate ${idx + 1}`}
                                style={{
                                  width: 48,
                                  height: 48,
                                  objectFit: 'cover',
                                  borderRadius: 6,
                                  cursor: 'pointer',
                                  border: '1px solid #e2e8f0',
                                }}
                                onClick={() => window.open(url, '_blank')}
                              />
                            ))}
                          </div>
                        ) : null}
                        <p style={{ margin: '10px 0 0 0', fontSize: 12, color: '#64748b' }}>
                          Status: <strong style={{ color: '#334155' }}>{req.profesionalEstimate.status}</strong>
                          {req.paymentStatus ? (
                            <>
                              {' '}
                              · Payment: <strong style={{ color: '#334155' }}>{req.paymentStatus}</strong>
                            </>
                          ) : null}
                        </p>
                        {estimateLocked ? (
                          <p style={{ margin: '10px 0 0 0', fontSize: 12, color: '#64748b', fontStyle: 'italic' }}>
                            This estimate was accepted or paid and cannot be edited.
                          </p>
                        ) : (
                          <button
                            type="button"
                            onClick={() => {
                              setEditingEstimate((prev) => ({ ...prev, [req.id]: true }));
                              setEstimateAmount((prev) => ({
                                ...prev,
                                [req.id]: req.profesionalEstimate.amount.toString(),
                              }));
                              setEstimateDescription((prev) => ({
                                ...prev,
                                [req.id]: req.profesionalEstimate.description || '',
                              }));
                              setEstimateImages((prev) => ({
                                ...prev,
                                [req.id]: req.profesionalEstimate.images || [],
                              }));
                              setProCardSectionsOpen((prev) => ({
                                ...prev,
                                [req.id]: { ...(prev[req.id] || {}), est: true },
                              }));
                            }}
                            style={{
                              marginTop: 12,
                              width: '100%',
                              padding: '10px 12px',
                              background: '#fff',
                              color: '#334155',
                              border: '1px solid #cbd5e1',
                              borderRadius: 8,
                              cursor: 'pointer',
                              fontSize: 13,
                              fontWeight: 600,
                            }}
                          >
                            Edit estimate
                          </button>
                        )}
                      </>
                    )
                  ) : (
                    <>
                      {!showEstimateForm[req.id] ? (
                        <button 
                          onClick={() => {
                            setShowEstimateForm((prev) => ({ ...prev, [req.id]: true }));
                            setProCardSectionsOpen((prev) => ({
                              ...prev,
                              [req.id]: { ...(prev[req.id] || {}), est: true },
                            }));
                          }}
                          style={{ 
                            width: '100%', 
                            marginTop: '12px',
                            padding: '10px 12px', 
                            background: '#fff', 
                            color: '#334155', 
                            border: '1px solid #cbd5e1', 
                            borderRadius: '8px', 
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '600'
                          }}
                        >
                          Submit estimate
                        </button>
                      ) : (
                        <>
                          <h4 style={{ margin: '0 0 12px 0', fontSize: 15, fontWeight: 700, color: '#0f172a' }}>Amount & details</h4>
                          
                          {/* Estimate Amount */}
                          <div style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', fontSize: '13px' }}>
                              Total Estimate Amount <span style={{ color: '#dc3545' }}>*</span>
                            </label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span style={{ fontSize: '16px', fontWeight: '600' }}>$</span>
                              <input
                                type="number"
                                step="0.01"
                                value={estimateAmount[req.id] || ''}
                                onChange={(e) => setEstimateAmount(prev => ({ ...prev, [req.id]: e.target.value }))}
                                placeholder="0.00"
                                style={{
                                  flex: 1,
                                  padding: '8px',
                                  border: '1px solid #ddd',
                                  borderRadius: '6px',
                                  fontSize: '14px',
                                  fontFamily: 'inherit'
                                }}
                              />
                            </div>
                          </div>

                          {/* Description */}
                          <div style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', fontSize: '13px' }}>
                              Description (optional)
                            </label>
                            <textarea
                              value={estimateDescription[req.id] || ''}
                              onChange={(e) => setEstimateDescription(prev => ({ ...prev, [req.id]: e.target.value }))}
                              placeholder="Describe the work to be done..."
                              style={{
                                width: '100%',
                                padding: '8px',
                                border: '1px solid #ddd',
                                borderRadius: '6px',
                                fontSize: '13px',
                                fontFamily: 'inherit',
                                minHeight: '60px',
                                resize: 'vertical'
                              }}
                            />
                          </div>

                          {/* Images */}
                          <div style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', fontSize: '13px' }}>
                              Photos (optional)
                            </label>
                            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                              <button
                                onClick={() => {
                                  const input = document.createElement('input');
                                  input.type = 'file';
                                  input.accept = 'image/*';
                                  input.capture = 'environment';
                                  input.onChange = (e) => {
                                    const files = Array.from(e.target.files || []);
                                    setEstimateImages(prev => ({
                                      ...prev,
                                      [req.id]: [...(prev[req.id] || []), ...files]
                                    }));
                                  };
                                  input.click();
                                }}
                                style={{
                                  flex: 1,
                                  padding: '10px',
                                  background: '#fff',
                                  color: '#334155',
                                  border: '1px solid #cbd5e1',
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                  fontSize: '13px',
                                  fontWeight: '600'
                                }}
                              >
                                Take photo
                              </button>
                              <button
                                onClick={() => {
                                  const input = document.createElement('input');
                                  input.type = 'file';
                                  input.accept = 'image/*';
                                  input.multiple = true;
                                  input.onChange = (e) => {
                                    const files = Array.from(e.target.files || []);
                                    setEstimateImages(prev => ({
                                      ...prev,
                                      [req.id]: [...(prev[req.id] || []), ...files]
                                    }));
                                  };
                                  input.click();
                                }}
                                style={{
                                  flex: 1,
                                  padding: '10px',
                                  background: '#fff',
                                  color: '#334155',
                                  border: '1px solid #cbd5e1',
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                  fontSize: '13px',
                                  fontWeight: '600'
                                }}
                              >
                                Add photos
                              </button>
                            </div>
                            {estimateImages[req.id] && estimateImages[req.id].length > 0 && (
                              <div style={{ marginTop: '8px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                {estimateImages[req.id].map((img, idx) => (
                                  <div key={idx} style={{ position: 'relative' }}>
                                    <img
                                      src={typeof img === 'string' ? img : URL.createObjectURL(img)}
                                      alt={`Upload ${idx + 1}`}
                                      style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                                    />
                                    <button
                                      onClick={() => {
                                        setEstimateImages(prev => ({
                                          ...prev,
                                          [req.id]: prev[req.id].filter((_, i) => i !== idx)
                                        }));
                                      }}
                                      style={{
                                        position: 'absolute',
                                        top: '-8px',
                                        right: '-8px',
                                        width: '20px',
                                        height: '20px',
                                        borderRadius: '50%',
                                        border: 'none',
                                        background: '#64748b',
                                        color: '#fff',
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                        fontWeight: 'bold'
                                      }}
                                    >×</button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* Buttons */}
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              onClick={() => handleSubmitEstimate(req.id)}
                              disabled={estimateUploading[req.id]}
                              style={{
                                flex: 1,
                                padding: '10px',
                                background: '#334155',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: estimateUploading[req.id] ? 'not-allowed' : 'pointer',
                                fontSize: '13px',
                                fontWeight: '600',
                                opacity: estimateUploading[req.id] ? 0.7 : 1,
                              }}
                            >
                              {estimateUploading[req.id] ? 
                                (editingEstimate[req.id] ? 'Updating...' : 'Submitting...') : 
                                (editingEstimate[req.id] ? 'Save estimate' : 'Submit estimate')
                              }
                            </button>
                            <button
                              onClick={() => {
                                setShowEstimateForm(prev => ({ ...prev, [req.id]: false }));
                                setEstimateAmount(prev => ({ ...prev, [req.id]: '' }));
                                setEstimateDescription(prev => ({ ...prev, [req.id]: '' }));
                                setEstimateImages(prev => ({ ...prev, [req.id]: [] }));
                              }}
                              style={{
                                flex: 1,
                                padding: '10px',
                                background: '#fff',
                                color: '#334155',
                                border: '1px solid #cbd5e1',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '13px',
                                fontWeight: '600'
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </>
                      )}
                    </>
                  )}
                  </ProCollapsiblePanel>
                </div>
              );
            })}
        </div>
      )}
    </div>
  );

  const renderJobsWorkbench = () => {
    const nAvail = availableRequests.length;
    const nProg = inProgressRequests.length;
    const nSched = scheduledRequestsList.length;
    const nPaid = paidReadyJobs.length;

    const chip = (id, label, count) => (
      <button
        type="button"
        key={id}
        className={jobsFilter === id ? 'td-job-filter-chip td-job-filter-chip-active' : 'td-job-filter-chip'}
        onClick={() => setJobsFilter(id)}
        style={{
          padding: '10px 18px',
          borderRadius: 999,
          border: jobsFilter === id ? '2px solid #6366f1' : '1px solid #e5e7eb',
          background: jobsFilter === id ? '#eef2ff' : '#fff',
          color: jobsFilter === id ? '#4338ca' : '#374151',
          fontWeight: 600,
          fontSize: 14,
          cursor: 'pointer',
          boxShadow: jobsFilter === id ? '0 2px 8px rgba(99, 102, 241, 0.2)' : '0 1px 2px rgba(0,0,0,0.05)',
        }}
      >
        {label}
        {count > 0 ? <span style={{ opacity: 0.85 }}> ({count})</span> : null}
      </button>
    );

    return (
      <div>
        <div style={{ marginBottom: 20 }}>
          <h2 style={{ margin: '0 0 8px 0', fontSize: 26, fontWeight: 800, color: '#111827' }}>My jobs</h2>
          <p style={{ color: '#64748b', margin: '0 0 18px 0', fontSize: 15, lineHeight: 1.5, maxWidth: 640 }}>
            Browse open jobs, suggest visit times, then run confirmed visits — use the filters for each stage.
          </p>
          <div
            className="td-jobs-sticky-toolbar"
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 10,
              position: 'sticky',
              top: 0,
              zIndex: 6,
              background: 'linear-gradient(180deg, #ffffff 70%, rgba(255,255,255,0.92) 100%)',
              padding: '12px 0 16px',
              marginBottom: 8,
              borderBottom: '1px solid #e5e7eb',
            }}
          >
            {chip('available', 'Open jobs', nAvail)}
            {chip('in_progress', 'In progress', nProg)}
            {chip('scheduled', 'Scheduled', nSched)}
            {chip('completed', 'Completed', nPaid)}
          </div>
        </div>
        {jobsFilter === 'available' && renderAvailableRequests(true)}
        {jobsFilter === 'in_progress' && renderAcceptedRequests(true)}
        {jobsFilter === 'scheduled' && renderScheduledRequests(true)}
        {jobsFilter === 'completed' && renderCompletedJobs()}
      </div>
    );
  };

  // Completed (paid) jobs ready to be worked on
  const renderCompletedJobs = () => (
    <div>
      <h2 style={{ marginBottom: '10px' }}>✓ Paid & Ready to Work</h2>
      <p style={{ color: '#666', marginBottom: '30px' }}>
        Owner has paid your estimate. After you finish on site, describe what you did and optionally add photos of the
        finished work — then mark the job complete. (Estimate photos from earlier are only for pricing.)
      </p>
      
      {paidReadyJobs.length === 0 ? (
        <div style={{ background: '#f9fafb', padding: '40px', borderRadius: '12px', textAlign: 'center', border: '1px solid #e5e7eb' }}>
          <p style={{ color: '#666', fontSize: '15px' }}>No paid jobs yet</p>
          <p style={{ color: '#999', fontSize: '13px' }}>Once an owner accepts and pays for your estimate, it will appear here</p>
        </div>
      ) : (
        <div className="requests-grid">
          {paidReadyJobs.map(job => (
            <div 
              key={job.id} 
              className="request-card" 
              style={{
                padding: '20px', 
                border: '2px solid #10b981', 
                borderRadius: '12px', 
                background: '#fff',
                boxShadow: '0 2px 8px rgba(16, 185, 129, 0.1)'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>{job.category}</h3>
                <span style={{ background: '#10b981', color: '#fff', padding: '4px 10px', borderRadius: '4px', fontSize: '12px', fontWeight: '600' }}>💰 Paid</span>
              </div>
              
              <p style={{ color: '#666', margin: '8px 0', fontSize: '14px' }}><strong>Unit:</strong> {job.unitNumber}</p>
              <p style={{ color: '#666', margin: '8px 0', fontSize: '13px' }}>{job.description}</p>
              
              {/* Payment Info */}
              <div style={{ background: '#d4edda', padding: '12px', borderRadius: '6px', marginTop: '12px', marginBottom: '12px', border: '1px solid #c3e6cb' }}>
                <p style={{ margin: '0 0 6px 0', fontSize: '12px', fontWeight: '600', color: '#155724' }}>✓ Payment Captured</p>
                <p style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: '600', color: '#155724' }}>
                  Your payout (after platform fee): $
                  {professionalNetPayoutDollars(job, proPayoutFeePct).toFixed(2)}
                </p>
                <p style={{ margin: 0, fontSize: '12px', color: '#166534' }}>
                  Job total (owner paid): ${grossJobAmountFromMaintenanceRequest(job).toFixed(2)}
                </p>
              </div>

              {/* Scheduled Time if available */}
              {job.acceptedMeetingTime && (
                <div style={{ background: '#e3f2fd', padding: '10px', borderRadius: '6px', marginBottom: '12px', border: '1px solid #bbdefb' }}>
                  <p style={{ margin: '0 0 4px 0', fontSize: '12px', fontWeight: '600', color: '#0d47a1' }}>📅 Scheduled:</p>
                  <p style={{ margin: 0, fontSize: '13px', color: '#0d47a1', fontWeight: '600' }}>
                    {formatMeetingOptionForViewer(job.acceptedMeetingTime.date, job.acceptedMeetingTime.time)}
                  </p>
                </div>
              )}

              <div
                style={{
                  marginBottom: '14px',
                  padding: '14px',
                  background: '#f8fafc',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                }}
              >
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                  What did you complete? <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <textarea
                  value={completionNoteByJobId[job.id] || ''}
                  onChange={(e) =>
                    setCompletionNoteByJobId((prev) => ({
                      ...prev,
                      [job.id]: e.target.value.slice(0, PAID_JOB_COMPLETION_NOTE_MAX_LEN),
                    }))
                  }
                  disabled={completingPaidJobId === job.id}
                  placeholder="e.g. Replaced faulty fill valve and supply line, tested for leaks, caulked base."
                  rows={4}
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    fontSize: '14px',
                    lineHeight: 1.45,
                    resize: 'vertical',
                    minHeight: '88px',
                  }}
                />
                <p style={{ margin: '6px 0 0', fontSize: '11px', color: '#64748b' }}>
                  Minimum {PAID_JOB_COMPLETION_NOTE_MIN_LEN} characters · {PAID_JOB_COMPLETION_NOTE_MAX_LEN} max
                </p>

                <p style={{ margin: '14px 0 8px', fontSize: '12px', fontWeight: 700, color: '#334155' }}>
                  Photos of finished work (optional)
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                  <button
                    type="button"
                    disabled={completingPaidJobId === job.id}
                    onClick={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = 'image/*';
                      input.capture = 'environment';
                      input.onchange = (ev) => {
                        const file = ev.target.files?.[0];
                        if (!file) return;
                        setCompletionImagesByJobId((prev) => {
                          const cur = prev[job.id] || [];
                          if (cur.length >= PAID_JOB_COMPLETION_MAX_PHOTOS) return prev;
                          return { ...prev, [job.id]: [...cur, file] };
                        });
                      };
                      input.click();
                    }}
                    style={{
                      padding: '8px 12px',
                      fontSize: '13px',
                      fontWeight: 600,
                      borderRadius: '8px',
                      border: '1px solid #cbd5e1',
                      background: '#fff',
                      cursor: completingPaidJobId === job.id ? 'not-allowed' : 'pointer',
                    }}
                  >
                    Take photo
                  </button>
                  <button
                    type="button"
                    disabled={completingPaidJobId === job.id}
                    onClick={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = 'image/*';
                      input.multiple = true;
                      input.onchange = (ev) => {
                        const files = Array.from(ev.target.files || []);
                        if (!files.length) return;
                        setCompletionImagesByJobId((prev) => {
                          const cur = prev[job.id] || [];
                          const room = PAID_JOB_COMPLETION_MAX_PHOTOS - cur.length;
                          const next = [...cur, ...files.slice(0, Math.max(0, room))];
                          return { ...prev, [job.id]: next };
                        });
                      };
                      input.click();
                    }}
                    style={{
                      padding: '8px 12px',
                      fontSize: '13px',
                      fontWeight: 600,
                      borderRadius: '8px',
                      border: '1px solid #cbd5e1',
                      background: '#fff',
                      cursor: completingPaidJobId === job.id ? 'not-allowed' : 'pointer',
                    }}
                  >
                    Add photos
                  </button>
                  <span style={{ fontSize: '12px', color: '#64748b' }}>
                    Up to {PAID_JOB_COMPLETION_MAX_PHOTOS} images
                  </span>
                </div>
                {completionImagesByJobId[job.id]?.length > 0 ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px' }}>
                    {completionImagesByJobId[job.id].map((img, idx) => (
                      <div key={idx} style={{ position: 'relative', width: 72, height: 72 }}>
                        <img
                          src={typeof img === 'string' ? img : URL.createObjectURL(img)}
                          alt=""
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0',
                          }}
                        />
                        <button
                          type="button"
                          disabled={completingPaidJobId === job.id}
                          onClick={() =>
                            setCompletionImagesByJobId((prev) => ({
                              ...prev,
                              [job.id]: (prev[job.id] || []).filter((_, i) => i !== idx),
                            }))
                          }
                          style={{
                            position: 'absolute',
                            top: -6,
                            right: -6,
                            width: 22,
                            height: 22,
                            borderRadius: '50%',
                            border: 'none',
                            background: '#1e293b',
                            color: '#fff',
                            fontSize: 12,
                            lineHeight: '22px',
                            cursor: completingPaidJobId === job.id ? 'not-allowed' : 'pointer',
                          }}
                          aria-label="Remove photo"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>

              <button
                type="button"
                disabled={completingPaidJobId === job.id}
                onClick={() => markPaidJobCompleteWithEvidence(job)}
                style={{
                  width: '100%',
                  padding: '10px',
                  background: completingPaidJobId === job.id ? '#6ee7b7' : '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: completingPaidJobId === job.id ? 'wait' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  opacity: completingPaidJobId === job.id ? 0.85 : 1,
                }}
              >
                {completingPaidJobId === job.id ? 'Saving…' : '✓ Mark Job Complete'}
              </button>
              {markCompleteOkJobId === job.id ? (
                <p style={{ margin: '10px 0 0', fontSize: '13px', fontWeight: 600, color: '#047857' }}>
                  Job marked complete. It will appear under Getting paid when the list refreshes.
                </p>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Getting paid tab (activeTab === 'earnings'): Stripe-verified totals + embedded Bank & deposits (Stripe Express)
  const renderEarningsSection = () => {
    const hasStripe = Boolean(connectAccountId);
    const stripeLifetimeDollars =
      typeof profData?.stripeLifetimeNetCents === 'number' && Number.isFinite(profData.stripeLifetimeNetCents)
        ? Math.round(profData.stripeLifetimeNetCents) / 100
        : null;
    const stripeTxn =
      typeof profData?.stripeLifetimeTxnCount === 'number' && Number.isFinite(profData.stripeLifetimeTxnCount)
        ? profData.stripeLifetimeTxnCount
        : null;

    return (
    <div>
      <h2 style={{ marginBottom: '8px' }}>{'\uD83D\uDCB0'} Getting paid</h2>
      <p style={{ margin: '0 0 24px 0', fontSize: 15, color: '#64748b', lineHeight: 1.5, maxWidth: 720 }}>
        Verified totals, your bank connection (Stripe Express), and per-job history — all in one place.
      </p>

      {hasStripe ? (
        <div
          style={{
            background: 'linear-gradient(135deg, #1e3a5f, #2563eb)',
            color: '#fff',
            padding: '30px',
            borderRadius: '12px',
            marginBottom: '24px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
          }}
        >
          <p style={{ margin: '0 0 8px 0', fontSize: '14px', opacity: 0.92 }}>Verified lifetime total (Stripe)</p>
          <p style={{ margin: '0 0 12px 0', fontSize: '12px', opacity: 0.88, lineHeight: 1.45 }}>
            Connect account balance activity (bank payouts excluded; refunds and fees match Stripe).
          </p>
          {stripeEarningsError ? (
            <div
              style={{
                marginBottom: '12px',
                padding: '12px',
                borderRadius: '8px',
                background: 'rgba(254,226,226,0.95)',
                color: '#991b1b',
                fontSize: '13px',
              }}
            >
              {stripeEarningsError}
            </div>
          ) : null}
          {stripeEarningsWarning ? (
            <div
              style={{
                marginBottom: '12px',
                padding: '12px',
                borderRadius: '8px',
                background: 'rgba(254,243,199,0.95)',
                color: '#92400e',
                fontSize: '13px',
              }}
            >
              {stripeEarningsWarning}
            </div>
          ) : null}
          <h1 style={{ margin: 0, fontSize: '48px', fontWeight: 'bold', opacity: stripeEarningsLoading ? 0.45 : 1 }}>
            {stripeEarningsLoading && stripeLifetimeDollars == null
              ? '…'
              : `$${(stripeLifetimeDollars ?? 0).toFixed(2)}`}
          </h1>
          <p style={{ margin: '8px 0 0 0', fontSize: '13px', opacity: 0.9 }}>
            {stripeTxn != null ? `${stripeTxn} Stripe balance line${stripeTxn !== 1 ? 's' : ''}` : '—'}{' '}
            {stripeEarningsSyncedLabel ? `· Last synced ${stripeEarningsSyncedLabel}` : ''}
          </p>
          <button
            type="button"
            onClick={() => fetchStripeLifetimeEarnings()}
            disabled={stripeEarningsLoading}
            style={{
              marginTop: '16px',
              padding: '10px 18px',
              fontSize: '14px',
              fontWeight: 600,
              color: '#1e3a5f',
              background: '#fff',
              border: 'none',
              borderRadius: '8px',
              cursor: stripeEarningsLoading ? 'wait' : 'pointer',
              opacity: stripeEarningsLoading ? 0.75 : 1,
            }}
          >
            {stripeEarningsLoading ? 'Refreshing from Stripe…' : 'Refresh from Stripe'}
          </button>
        </div>
      ) : (
        <div
          style={{
            background: '#f8fafc',
            padding: '16px 20px',
            borderRadius: '10px',
            border: '1px solid #e2e8f0',
            marginBottom: '24px',
            fontSize: '14px',
            color: '#475569',
          }}
        >
          Connect your payout account below to load a verified total from Stripe.
        </div>
      )}

      {renderStripeConnectSection({ embedded: true })}

      {completedJobs.length > 0 ? (
        <div style={{ background: '#fff', padding: '20px', borderRadius: '8px', border: '1px solid #ddd' }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: '18px' }}>Job history (this app)</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            {completedJobs.map((job) => {
              const gross = grossJobAmountFromMaintenanceRequest(job);
              const net = professionalNetPayoutDollars(job, proPayoutFeePct);
              return (
              <div key={job.id} style={{
                padding: '15px',
                background: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div style={{ flex: 1 }}>
                  <p style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '600', color: '#333' }}>
                    {job.category}
                  </p>
                  <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#666' }}>
                    📍 {job.tenantInfo?.address || 'Unit ' + job.tenantInfo?.unitNumber}
                  </p>
                  <p style={{ margin: '0', fontSize: '11px', color: '#999' }}>
                    Completed: {job.completedAt ? new Date(job.completedAt.toDate ? job.completedAt.toDate() : job.completedAt).toLocaleDateString() : 'Recent'}
                  </p>
                  {job.professionalCompletion?.summary ? (
                    <p style={{ margin: '10px 0 0', fontSize: '12px', color: '#475569', lineHeight: 1.45 }}>
                      <strong style={{ color: '#334155' }}>Work logged:</strong> {job.professionalCompletion.summary}
                    </p>
                  ) : null}
                  {Array.isArray(job.professionalCompletion?.imageUrls) && job.professionalCompletion.imageUrls.length > 0 ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                      {job.professionalCompletion.imageUrls.map((u, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => window.open(u, '_blank', 'noopener,noreferrer')}
                          style={{ padding: 0, border: 'none', background: 'none', cursor: 'pointer' }}
                        >
                          <img
                            src={u}
                            alt=""
                            style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 6, border: '1px solid #e5e7eb' }}
                          />
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
                <div style={{
                  textAlign: 'right',
                  paddingLeft: '20px',
                  borderLeft: '2px solid #e5e7eb'
                }}>
                  <p style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#059669' }}>
                    ${net.toFixed(2)}
                  </p>
                  <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#64748b', fontWeight: '500' }}>
                    Job total ${gross.toFixed(2)}
                  </p>
                  <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#059669', fontWeight: '600' }}>
                    ✓ Ready to Payout
                  </p>
                </div>
              </div>
              );
            })}
          </div>
          <div style={{
            marginTop: '20px',
            padding: '15px',
            background: '#d1fae5',
            border: '1px solid #6ee7b7',
            borderRadius: '6px',
            textAlign: 'center'
          }}>
            <p style={{ margin: 0, fontSize: '14px', color: '#059669', fontWeight: '600' }}>
              📧 Contact us to request a payout
            </p>
            <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#047857' }}>
              Email: support@tenantdispatch.com
            </p>
          </div>
        </div>
      ) : (
        <p style={{ margin: 0, fontSize: 14, color: '#64748b', lineHeight: 1.55 }}>
          No completed jobs in this workspace. Per-job payouts appear here when you finish work in TenantDispatch; the
          amount above still reflects your Stripe account.
        </p>
      )}
    </div>
    );
  };

  // Profile section
  const renderProfileSection = () => (
    <div>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '16px',
          marginBottom: '30px',
        }}
      >
        <div style={{ flex: '1 1 220px', minWidth: 0 }}>
          <h2 style={{ margin: '0 0 8px 0' }}>⚙️ My Professional Profile</h2>
          <p style={{ color: '#666', margin: 0, fontSize: '14px' }}>Manage your service information and preferences</p>
        </div>
        {!editingProfile && (
          <button
            type="button"
            className="btn-primary"
            onClick={() => setEditingProfile(true)}
            style={{
              width: 'auto',
              minWidth: 'auto',
              maxWidth: 'none',
              flex: '0 0 auto',
              alignSelf: 'center',
              padding: '10px 22px',
              whiteSpace: 'nowrap',
            }}
          >
            ✏️ Edit Profile
          </button>
        )}
      </div>

      <DashboardAppearanceSettings style={{ marginBottom: '24px' }} />

      {editMessage && (
        <div style={{ 
          padding: '12px 16px', 
          borderRadius: '6px', 
          marginBottom: '20px', 
          background: editMessage.includes('✓') ? '#dcfce7' : '#fee2e2',
          color: editMessage.includes('✓') ? '#166534' : '#991b1b',
          fontSize: '14px',
          border: `1px solid ${editMessage.includes('✓') ? '#86efac' : '#fca5a5'}`
        }}>
          {editMessage}
        </div>
      )}

      {editingProfile ? (
        <div
          className="pro-profile-edit-panel"
          style={{ background: '#fff', padding: '30px', borderRadius: '12px', border: '1px solid #e5e7eb', maxWidth: '700px' }}
        >
          <div className="form-group">
            <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>Email Address</label>
            <input
              type="email"
              value={editEmail}
              onChange={(e) => setEditEmail(e.target.value)}
              placeholder="your.email@example.com"
              className="input-field"
            />
          </div>

          <div className="form-group">
            <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>Business Name</label>
            <input
              type="text"
              value={editBusinessName}
              onChange={(e) => setEditBusinessName(e.target.value)}
              placeholder="Your business name"
              className="input-field"
            />
          </div>

          <div className="form-group">
            <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>Service Categories</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
              {REPAIR_CATEGORIES.map(cat => (
                <label key={cat} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', padding: '8px', border: '1px solid #ddd', borderRadius: '6px', transition: 'all 0.2s', backgroundColor: editCategories.includes(cat) ? '#e3f2fd' : '#fff' }}>
                  <input
                    type="checkbox"
                    checked={editCategories.includes(cat)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setEditCategories([...editCategories, cat]);
                      } else {
                        setEditCategories(editCategories.filter(c => c !== cat));
                      }
                    }}
                    style={{ marginRight: '8px', cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '13px' }}>{cat}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>Service Area (City/Region)</label>
            <AddressAutocomplete
              value={editServiceArea}
              onChange={(addressInput) => {
                if (typeof addressInput === 'object' && addressInput.address) {
                  setEditServiceArea(addressInput.address);
                } else {
                  setEditServiceArea(addressInput);
                }
              }}
              placeholder="Search and select city or address"
            />
          </div>

          <div className="form-group">
            <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>Service Radius (miles)</label>
            <input
              type="number"
              value={editServiceRadius}
              onChange={(e) => setEditServiceRadius(e.target.value)}
              min="1"
              max="100"
              className="input-field"
            />
          </div>

          <div className="form-group">
            <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>Service Fee per Visit ($)</label>
            <input
              type="number"
              value={editServiceFee}
              onChange={(e) => setEditServiceFee(e.target.value)}
              placeholder="100"
              min="0"
              step="0.01"
              className="input-field"
            />
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button 
              className="btn-primary"
              onClick={handleSaveProfile}
              style={{ flex: 1 }}
            >
              ✓ Save Changes
            </button>
            <button 
              className="btn-small cancel"
              onClick={handleCancelEdit}
              style={{ flex: 1 }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div style={{ background: '#fff', padding: '30px', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '30px' }}>
            <div>
              <p style={{ color: '#999', fontSize: '12px', marginBottom: '4px', fontWeight: '600' }}>BUSINESS NAME</p>
              <p style={{ fontSize: '16px', fontWeight: '500', margin: 0 }}>{profData?.businessName || 'Not set'}</p>
            </div>
            <div>
              <p style={{ color: '#999', fontSize: '12px', marginBottom: '4px', fontWeight: '600' }}>SERVICE AREA</p>
              <p style={{ fontSize: '16px', fontWeight: '500', margin: 0 }}>{profData?.serviceArea || 'Not set'}</p>
            </div>
            <div>
              <p style={{ color: '#999', fontSize: '12px', marginBottom: '4px', fontWeight: '600' }}>SERVICE RADIUS</p>
              <p style={{ fontSize: '16px', fontWeight: '500', margin: 0 }}>{profData?.serviceRadius || '0'} miles</p>
            </div>
            <div>
              <p style={{ color: '#999', fontSize: '12px', marginBottom: '4px', fontWeight: '600' }}>SERVICE FEE</p>
              <p style={{ fontSize: '16px', fontWeight: '500', margin: 0 }}>${profData?.serviceFee || '0'}</p>
            </div>
          </div>

          <div style={{ marginTop: '30px', paddingTop: '30px', borderTop: '1px solid #e5e7eb' }}>
            <p style={{ color: '#999', fontSize: '12px', marginBottom: '12px', fontWeight: '600' }}>SERVICE CATEGORIES</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {profData?.categories && profData.categories.length > 0 ? (
                profData.categories.map(cat => (
                  <span key={cat} style={{ background: '#e3f2fd', color: '#1976d2', padding: '6px 12px', borderRadius: '20px', fontSize: '13px', fontWeight: '500' }}>
                    {cat}
                  </span>
                ))
              ) : (
                <p style={{ color: '#999', fontSize: '13px', margin: 0 }}>No categories selected</p>
              )}
            </div>
          </div>

          <div style={{ marginTop: '30px', paddingTop: '30px', borderTop: '1px solid #e5e7eb' }}>
            <p style={{ color: '#999', fontSize: '12px', marginBottom: '12px', fontWeight: '600' }}>
              {`TENANT & OWNER REVIEWS`}
            </p>
            {(profData?.tenantRatingCount > 0 || profData?.ownerRatingCount > 0) && (
              <p style={{ fontSize: '13px', color: '#475569', margin: '0 0 12px', lineHeight: 1.5 }}>
                <strong>Live averages</strong> (for owners/tenants when booking):{' '}
                {profData?.tenantRatingCount > 0
                  ? `Clients ★${(profData.tenantRatingSum / profData.tenantRatingCount).toFixed(1)} (${profData.tenantRatingCount})`
                  : 'No client ratings yet'}
                {' · '}
                {profData?.ownerRatingCount > 0
                  ? `Owners ★${(profData.ownerRatingSum / profData.ownerRatingCount).toFixed(1)} (${profData.ownerRatingCount})`
                  : 'No owner ratings yet'}
              </p>
            )}
            {tenantReviewJobs.length === 0 ? (
              <p style={{ color: '#999', fontSize: '13px', margin: 0 }}>
                No reviews yet. Clients and property owners can rate you after jobs are marked complete.
              </p>
            ) : (
              <>
                <p style={{ fontSize: '14px', color: '#334155', margin: '0 0 16px', fontWeight: 600 }}>
                  ★ {reviewStats.avg.toFixed(1)} average · {reviewStats.count} review
                  {reviewStats.count === 1 ? '' : 's'} (client + owner)
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxHeight: 360, overflowY: 'auto' }}>
                  {tenantReviewJobs.map((job) => {
                    const tr = job.tenantRating;
                    const ownerR = job.ownerRating;
                    const ts = normalizeRatingStars(tr);
                    const os = normalizeRatingStars(ownerR);
                    const whenT = tr?.createdAt?.toDate ? tr.createdAt.toDate().toLocaleDateString() : '';
                    const whenO = ownerR?.createdAt?.toDate ? ownerR.createdAt.toDate().toLocaleDateString() : '';
                    return (
                      <div
                        key={job.id}
                        style={{
                          padding: 14,
                          background: '#f8fafc',
                          borderRadius: 8,
                          border: '1px solid #e2e8f0',
                        }}
                      >
                        <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b', marginBottom: 8 }}>
                          {job.category || 'Request'}
                        </div>
                        {ts != null ? (
                          <div style={{ marginBottom: os != null ? 10 : 0 }}>
                            <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>Client </span>
                            <span style={{ fontSize: 13, color: '#ca8a04', letterSpacing: 1 }}>
                              {'★'.repeat(ts)}
                              {'☆'.repeat(5 - ts)}
                            </span>
                            {whenT ? (
                              <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 8 }}>{whenT}</span>
                            ) : null}
                            {tr.comment ? (
                              <p style={{ margin: '6px 0 0', fontSize: 13, color: '#475569', lineHeight: 1.45 }}>{tr.comment}</p>
                            ) : null}
                          </div>
                        ) : null}
                        {os != null ? (
                          <div>
                            <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>Owner </span>
                            <span style={{ fontSize: 13, color: '#ca8a04', letterSpacing: 1 }}>
                              {'★'.repeat(os)}
                              {'☆'.repeat(5 - os)}
                            </span>
                            {whenO ? (
                              <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 8 }}>{whenO}</span>
                            ) : null}
                            {ownerR.comment ? (
                              <p style={{ margin: '6px 0 0', fontSize: 13, color: '#475569', lineHeight: 1.45 }}>{ownerR.comment}</p>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const renderOverviewDashboard = () => {
    const nAvail = availableRequests.length;
    const nProg = inProgressRequests.length;
    const nSched = scheduledRequestsList.length;
    const nPaidPipeline = paidReadyJobs.length;
    const fmtMoney = (n) => `$${Number(n || 0).toFixed(2)}`;

    const tileBase = {
      borderRadius: 14,
      border: '1px solid #e5e7eb',
      background: '#ffffff',
      padding: '20px 22px',
      textAlign: 'left',
      boxShadow: '0 2px 8px rgba(15, 23, 42, 0.06)',
      transition: 'box-shadow 0.2s, transform 0.15s',
      minHeight: 128,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      boxSizing: 'border-box',
    };

    const TileButton = ({ onClick, accent, children }) => (
      <button
        type="button"
        className="td-pro-overview-tile request-card"
        onClick={onClick}
        style={{
          ...tileBase,
          cursor: 'pointer',
          borderLeft: accent ? `4px solid ${accent}` : undefined,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = '0 8px 24px rgba(99, 102, 241, 0.12)';
          e.currentTarget.style.transform = 'translateY(-2px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = tileBase.boxShadow;
          e.currentTarget.style.transform = 'none';
        }}
      >
        {children}
      </button>
    );

    const TileStat = ({ accent, children }) => (
      <div
        className="td-pro-overview-tile request-card"
        style={{
          ...tileBase,
          cursor: 'default',
          borderLeft: accent ? `4px solid ${accent}` : undefined,
        }}
      >
        {children}
      </div>
    );

    return (
      <div>
        <div style={{ marginBottom: 24 }}>
          <h2 style={{ margin: '0 0 8px 0', fontSize: 26, fontWeight: 800, color: '#111827' }}>Dashboard</h2>
          <p style={{ color: '#64748b', margin: 0, fontSize: 15, lineHeight: 1.55, maxWidth: 720 }}>
            Snapshot of your pipeline, earnings, and account tasks. Select a tile to jump to the right place.
          </p>
        </div>

        {!depositUnlocked ? (
          <div
            style={{
              marginBottom: 20,
              padding: '14px 18px',
              borderRadius: 12,
              border: '1px solid #fcd34d',
              background: '#fffbeb',
              color: '#92400e',
              fontSize: 14,
              lineHeight: 1.5,
            }}
          >
            <strong>Deposit required</strong> to work open jobs and see full request details. Open{' '}
            <strong>My jobs → Open jobs</strong> to complete the $20 refundable deposit (unless waived by the platform).
          </div>
        ) : null}

        {stripePayoutNeedsAttention ? (
          <div
            style={{
              marginBottom: 20,
              padding: '14px 18px',
              borderRadius: 12,
              border: '1px solid #c4b5fd',
              background: '#f5f3ff',
              color: '#5b21b6',
              fontSize: 14,
              lineHeight: 1.5,
            }}
          >
            <strong>Stripe Express</strong> is not ready to receive payouts yet. Open <strong>Getting paid</strong> in the
            sidebar to connect or finish setup.
          </div>
        ) : null}

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
            gap: 16,
            marginBottom: 28,
          }}
        >
          <TileButton
            accent="#6366f1"
            onClick={() => {
              setActiveTab('jobs');
              setJobsFilter('available');
              setMobileMenuOpen(false);
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 700, color: '#6366f1', letterSpacing: 0.3 }}>OPEN JOBS</div>
            <div style={{ fontSize: 34, fontWeight: 800, color: '#111827', margin: '8px 0 4px' }}>{nAvail}</div>
            <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.45 }}>
              {depositUnlocked ? 'Requests in your trades' : 'Count shown; pay deposit to unlock'}
            </div>
          </TileButton>

          <TileButton
            accent="#8b5cf6"
            onClick={() => {
              setActiveTab('jobs');
              setJobsFilter('in_progress');
              setMobileMenuOpen(false);
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 700, color: '#7c3aed', letterSpacing: 0.3 }}>IN PROGRESS</div>
            <div style={{ fontSize: 34, fontWeight: 800, color: '#111827', margin: '8px 0 4px' }}>{nProg}</div>
            <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.45 }}>Active — scheduling or estimates</div>
          </TileButton>

          <TileButton
            accent="#0284c7"
            onClick={() => {
              setActiveTab('jobs');
              setJobsFilter('scheduled');
              setMobileMenuOpen(false);
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 700, color: '#0369a1', letterSpacing: 0.3 }}>SCHEDULED</div>
            <div style={{ fontSize: 34, fontWeight: 800, color: '#111827', margin: '8px 0 4px' }}>{nSched}</div>
            <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.45 }}>Visits with a confirmed time</div>
          </TileButton>

          <TileButton
            accent="#10b981"
            onClick={() => {
              setActiveTab('jobs');
              setJobsFilter('completed');
              setMobileMenuOpen(false);
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 700, color: '#059669', letterSpacing: 0.3 }}>PAID — READY TO WORK</div>
            <div style={{ fontSize: 34, fontWeight: 800, color: '#111827', margin: '8px 0 4px' }}>{nPaidPipeline}</div>
            <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.45 }}>Payment captured; complete the job</div>
          </TileButton>

          <TileStat accent="#0d9488">
            <div style={{ fontSize: 13, fontWeight: 700, color: '#0f766e', letterSpacing: 0.3 }}>JOBS MARKED COMPLETE</div>
            <div style={{ fontSize: 34, fontWeight: 800, color: '#111827', margin: '8px 0 4px' }}>{finishedWorkCount}</div>
            <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.45 }}>Work finished (all time)</div>
          </TileStat>

          <TileButton
            accent={stripePayoutNeedsAttention ? '#f59e0b' : '#059669'}
            onClick={() => {
              setActiveTab('earnings');
              setMobileMenuOpen(false);
            }}
          >
            <div
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: stripePayoutNeedsAttention ? '#b45309' : '#047857',
                letterSpacing: 0.3,
              }}
            >
              GETTING PAID
            </div>
            <div style={{ fontSize: 28, fontWeight: 800, color: '#111827', margin: '8px 0 4px' }}>
              {fmtMoney(overviewNetEarningsDisplay.dollars)}
            </div>
            <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.45 }}>
              {stripePayoutNeedsAttention
                ? 'Finish Stripe Express setup · tap Getting paid'
                : overviewNetEarningsDisplay.hint}
            </div>
          </TileButton>

          {reviewStats.count > 0 ? (
            <TileButton
              accent="#ca8a04"
              onClick={() => {
                setActiveTab('profile');
                setMobileMenuOpen(false);
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 700, color: '#a16207', letterSpacing: 0.3 }}>REVIEWS</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#111827', margin: '8px 0 4px' }}>
                ★ {reviewStats.avg.toFixed(1)}
              </div>
              <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.45 }}>
                {reviewStats.count} rating{reviewStats.count === 1 ? '' : 's'} · see My Profile
              </div>
            </TileButton>
          ) : null}

          <TileButton
            accent="#64748b"
            onClick={() => {
              setActiveTab('profile');
              setMobileMenuOpen(false);
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 700, color: '#475569', letterSpacing: 0.3 }}>PROFILE & SETTINGS</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#111827', margin: '12px 0 6px', lineHeight: 1.35 }}>
              {profData?.businessName || 'Your business'}
            </div>
            <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.45 }}>Trades, area, appearance, reviews</div>
          </TileButton>
        </div>

        <div
          style={{
            padding: '16px 18px',
            borderRadius: 12,
            border: '1px solid #e2e8f0',
            background: '#f8fafc',
            fontSize: 13,
            color: '#475569',
            lineHeight: 1.55,
          }}
        >
          <strong style={{ color: '#334155' }}>Tip:</strong> Respond quickly on <strong>In progress</strong> requests — owners
          often choose the first pro who proposes clear times and a solid estimate.
        </div>
      </div>
    );
  };

  return (
    <div className="dashboard-container">
      {/* SIDEBAR */}
      <div className={`dashboard-sidebar ${mobileMenuOpen ? 'open' : ''}`}>
        <h2>🔧 Pro Dispatch</h2>
        {profData?.businessName && (
          <p
            style={{
              fontSize: '12px',
              color: '#bdc3c7',
              marginBottom: reviewStats.count > 0 ? '4px' : '30px',
            }}
          >
            {profData.businessName}
          </p>
        )}
        {reviewStats.count > 0 && (
          <p style={{ fontSize: '11px', color: '#fbbf24', margin: '0 0 24px', lineHeight: 1.35 }}>
            ★ {reviewStats.avg.toFixed(1)} · {reviewStats.count} review{reviewStats.count === 1 ? '' : 's'}
            {reviewStats.tenantCount > 0 || reviewStats.ownerCount > 0
              ? ` (${reviewStats.tenantCount} tenant / ${reviewStats.ownerCount} owner)`
              : ''}
          </p>
        )}

        {setupComplete && (
          <>
            <div
              className={`nav-item ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('overview');
                setMobileMenuOpen(false);
              }}
            >
              📊 Dashboard
            </div>
            <div
              className={`nav-item ${activeTab === 'jobs' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('jobs');
                setMobileMenuOpen(false);
              }}
            >
              🔧 My jobs
            </div>
            <div className={`nav-item ${activeTab === 'earnings' ? 'active' : ''}`} onClick={() => { setActiveTab('earnings'); setMobileMenuOpen(false); }}>
              {'💰'} Getting paid
            </div>
            <div className={`nav-item ${activeTab === 'profile' ? 'active' : ''}`} onClick={() => { setActiveTab('profile'); setMobileMenuOpen(false); }}>
              ⚙️ My Profile
            </div>
          </>
        )}

        <div className="logout-nav-item" onClick={() => { onLogout(); setMobileMenuOpen(false); }}>
          🚪 Logout
        </div>
        <div className="sidebar-footer">v1.0</div>
      </div>

      {/* MAIN CONTENT */}
      <div className={`dashboard-content ${mobileMenuOpen ? 'menu-open' : ''}`} onClick={(e) => {
        if (e.target === e.currentTarget) setMobileMenuOpen(false);
      }}>
        {/* Mobile menu toggle */}
        <button 
          className="mobile-menu-toggle"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          ☰
        </button>

        {setupComplete && unreadProJobAlerts.length > 0 ? (
          <div
            style={{
              margin: '12px 16px 0',
              padding: '14px 16px',
              borderRadius: 12,
              border: '1px solid #c7d2fe',
              background: 'linear-gradient(135deg, #eef2ff 0%, #faf5ff 100%)',
              maxWidth: 720,
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 12,
                marginBottom: 8,
                flexWrap: 'wrap',
              }}
            >
              <div style={{ minWidth: 0 }}>
                <strong style={{ fontSize: 15, color: '#312e81' }}>🔔 Job alerts</strong>
                <p style={{ margin: '6px 0 0', fontSize: 12, color: '#64748b', lineHeight: 1.4 }}>
                  Clear when you are done — the banner stays hidden until the next alert.
                </p>
              </div>
              <button
                type="button"
                onClick={() => markAllProJobAlertsRead()}
                style={{
                  flexShrink: 0,
                  padding: '8px 14px',
                  fontSize: 13,
                  fontWeight: 600,
                  borderRadius: 8,
                  border: '1px solid #a5b4fc',
                  background: '#fff',
                  color: '#4338ca',
                  cursor: 'pointer',
                }}
              >
                Dismiss all
              </button>
            </div>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, maxHeight: 240, overflowY: 'auto' }}>
              {unreadProJobAlerts.map((n, idx) => (
                <li
                  key={n.id}
                  style={{
                    padding: '10px 0',
                    borderTop: idx === 0 ? 'none' : '1px solid #e0e7ff',
                    fontSize: 14,
                    color: '#334155',
                    lineHeight: 1.45,
                  }}
                >
                  <div style={{ fontWeight: 700, color: '#1e1b4b' }}>{n.title}</div>
                  <div style={{ marginTop: 4 }}>{n.message}</div>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {!setupComplete ? (
          <div className="pro-onboarding-wizard">
            {reviewStats.count > 0 && (
              <div
                style={{
                  maxWidth: '860px',
                  margin: '0 auto 20px',
                  padding: '14px 16px',
                  background: '#fffbeb',
                  border: '1px solid #fcd34d',
                  borderRadius: '10px',
                }}
              >
                <p style={{ margin: 0, fontWeight: 700, fontSize: 14, color: '#92400e' }}>You have new reviews</p>
                <p style={{ margin: '8px 0 0', fontSize: 13, color: '#78350f', lineHeight: 1.5 }}>
                  ★ {reviewStats.avg.toFixed(1)} average from {reviewStats.count} rating
                  {reviewStats.count === 1 ? '' : 's'} (client + owner). After onboarding, open{' '}
                  <strong>My Profile</strong> in the sidebar for full details.
                </p>
              </div>
            )}

            <div className="pro-onboarding-progress">
              {ONBOARDING_STEPS.map((s) => (
                <div
                  key={s.num}
                  className={`pro-onboarding-progress-step ${onboardingStep === s.num ? 'is-active' : ''} ${onboardingStep > s.num ? 'is-completed' : ''}`}
                >
                  <div className="pro-onboarding-progress-circle">
                    {onboardingStep > s.num ? '✓' : s.num}
                  </div>
                  <span className="pro-onboarding-progress-label">{s.label}</span>
                </div>
              ))}
            </div>

            {onboardingStep === 1 && (
              <div className="pro-onboarding-hero">
                <h2 className="pro-onboarding-hero-title">What kind of work do you do?</h2>
                <p className="pro-onboarding-hero-subtitle">
                  Select every trade that applies. You will see maintenance requests that match these categories.
                </p>
                <div className="pro-onboarding-trade-grid">
                  {REPAIR_CATEGORIES.map((cat) => {
                    const meta = TRADE_META[cat] || { icon: '📦', requiresLicense: false, tradeType: 'general' };
                    const selected = wizardTrades.includes(cat);
                    return (
                      <button
                        key={cat}
                        type="button"
                        className={`pro-onboarding-trade-card ${selected ? 'is-selected' : ''}`}
                        onClick={() => {
                          setWizardTrades((prev) =>
                            prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]
                          );
                        }}
                      >
                        <span className="pro-onboarding-trade-icon">{meta.icon}</span>
                        <span className="pro-onboarding-trade-name">{cat}</span>
                        {meta.requiresLicense && (
                          <span className="pro-onboarding-trade-badge">License required</span>
                        )}
                        {selected && <span className="pro-onboarding-trade-check">✓</span>}
                      </button>
                    );
                  })}
                </div>
                <div className="pro-onboarding-actions">
                  <button
                    type="button"
                    className="btn-primary"
                    disabled={wizardTrades.length === 0}
                    onClick={handleWizardNext}
                  >
                    Continue
                  </button>
                </div>
              </div>
            )}

            {onboardingStep === 2 && !wizardGated && (() => {
              const licensedTrades = wizardTrades.filter((t) => TRADE_META[t]?.requiresLicense);
              const isHandyman = wizardTrades.includes('General Maintenance (Handyman)');
              const hasLicensedTrades = licensedTrades.length > 0;
              const generalTrades = wizardTrades.filter((t) => TRADE_META[t]?.tradeType === 'general');

              return (
                <div className="pro-onboarding-hero">
                  <h2 className="pro-onboarding-hero-title">Tell us about your qualifications</h2>
                  <p className="pro-onboarding-hero-subtitle">
                    This helps us match you with the right jobs and build trust with property owners.
                  </p>

                  {hasLicensedTrades && (
                    <div className="pro-onboarding-qual-section">
                      <h3 className="pro-onboarding-qual-heading">Licensed trades: {licensedTrades.join(', ')}</h3>
                      <div className="pro-onboarding-qual-row">
                        <label className="pro-onboarding-qual-label">Do you hold an active license?</label>
                        <div className="pro-onboarding-toggle-group">
                          <button
                            type="button"
                            className={`pro-onboarding-toggle ${qualHasLicense === true ? 'is-active' : ''}`}
                            onClick={() => setQualHasLicense(true)}
                          >
                            Yes
                          </button>
                          <button
                            type="button"
                            className={`pro-onboarding-toggle ${qualHasLicense === false ? 'is-active' : ''}`}
                            onClick={() => setQualHasLicense(false)}
                          >
                            No
                          </button>
                        </div>
                      </div>
                      <div className="pro-onboarding-qual-row">
                        <label className="pro-onboarding-qual-label">Do you carry general liability insurance?</label>
                        <div className="pro-onboarding-toggle-group">
                          <button
                            type="button"
                            className={`pro-onboarding-toggle ${qualHasInsurance === true ? 'is-active' : ''}`}
                            onClick={() => setQualHasInsurance(true)}
                          >
                            Yes
                          </button>
                          <button
                            type="button"
                            className={`pro-onboarding-toggle ${qualHasInsurance === false ? 'is-active' : ''}`}
                            onClick={() => setQualHasInsurance(false)}
                          >
                            No
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {isHandyman && (
                    <div className="pro-onboarding-qual-section">
                      <h3 className="pro-onboarding-qual-heading">General Maintenance / Handyman</h3>
                      <div className="pro-onboarding-qual-row">
                        <label className="pro-onboarding-qual-label">Years of experience</label>
                        <select
                          className="input-field"
                          value={qualYearsExperience}
                          onChange={(e) => setQualYearsExperience(e.target.value)}
                        >
                          <option value="">Select...</option>
                          {YEARS_OPTIONS.map((y) => (
                            <option key={y} value={y}>
                              {y}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="pro-onboarding-qual-row">
                        <label className="pro-onboarding-qual-label">Top specialties</label>
                        <input
                          type="text"
                          className="input-field"
                          value={qualSpecialties}
                          onChange={(e) => setQualSpecialties(e.target.value)}
                          placeholder="e.g. drywall patching, furniture assembly, minor plumbing"
                        />
                      </div>
                      <div className="pro-onboarding-qual-row">
                        <label className="pro-onboarding-qual-label">Do you have references or a portfolio?</label>
                        <div className="pro-onboarding-toggle-group">
                          <button
                            type="button"
                            className={`pro-onboarding-toggle ${qualHasReferences === true ? 'is-active' : ''}`}
                            onClick={() => setQualHasReferences(true)}
                          >
                            Yes
                          </button>
                          <button
                            type="button"
                            className={`pro-onboarding-toggle ${qualHasReferences === false ? 'is-active' : ''}`}
                            onClick={() => setQualHasReferences(false)}
                          >
                            No
                          </button>
                        </div>
                        {qualHasReferences && (
                          <input
                            type="url"
                            className="input-field"
                            value={qualReferencesLink}
                            onChange={(e) => setQualReferencesLink(e.target.value)}
                            placeholder="Link to portfolio or references (optional)"
                            style={{ marginTop: 8 }}
                          />
                        )}
                      </div>
                    </div>
                  )}

                  {!hasLicensedTrades && !isHandyman && generalTrades.length > 0 && (
                    <div className="pro-onboarding-qual-section">
                      <h3 className="pro-onboarding-qual-heading">Your trades: {generalTrades.join(', ')}</h3>
                      <div className="pro-onboarding-qual-row">
                        <label className="pro-onboarding-qual-label">
                          Do you carry general liability insurance? (recommended)
                        </label>
                        <div className="pro-onboarding-toggle-group">
                          <button
                            type="button"
                            className={`pro-onboarding-toggle ${qualHasInsurance === true ? 'is-active' : ''}`}
                            onClick={() => setQualHasInsurance(true)}
                          >
                            Yes
                          </button>
                          <button
                            type="button"
                            className={`pro-onboarding-toggle ${qualHasInsurance === false ? 'is-active' : ''}`}
                            onClick={() => setQualHasInsurance(false)}
                          >
                            No
                          </button>
                        </div>
                      </div>
                      <div className="pro-onboarding-qual-row">
                        <label className="pro-onboarding-qual-label">Years of experience</label>
                        <select
                          className="input-field"
                          value={qualYearsExperience}
                          onChange={(e) => setQualYearsExperience(e.target.value)}
                        >
                          <option value="">Select...</option>
                          {YEARS_OPTIONS.map((y) => (
                            <option key={y} value={y}>
                              {y}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}

                  <div className="pro-onboarding-actions">
                    <button type="button" className="pro-onboarding-back" onClick={handleWizardBack}>
                      Back
                    </button>
                    <button type="button" className="btn-primary" onClick={handleWizardNext}>
                      Continue
                    </button>
                  </div>
                </div>
              );
            })()}

            {onboardingStep === 2 && wizardGated && (
              <div className="pro-onboarding-hero">
                <div className="pro-onboarding-gate">
                  <div className="pro-onboarding-gate-icon">📋</div>
                  <h2 className="pro-onboarding-gate-title">We will keep you posted</h2>
                  <p className="pro-onboarding-gate-text">
                    Thanks for your interest in Tenant Dispatch! For trades like{' '}
                    <strong>{wizardTrades.filter((t) => TRADE_META[t]?.requiresLicense).join(', ')}</strong>, we
                    currently require an active license and general liability insurance to protect property owners and
                    residents.
                  </p>
                  <p className="pro-onboarding-gate-text">
                    We have saved your information and will reach out if our requirements change. Email{' '}
                    <strong>hello@tenantdispatch.com</strong> with any questions.
                  </p>
                  <div className="pro-onboarding-actions">
                    <button type="button" className="pro-onboarding-back" onClick={handleWizardBack}>
                      Go back and edit trades
                    </button>
                  </div>
                </div>
              </div>
            )}

            {onboardingStep === 3 && (
              <div className="pro-onboarding-hero">
                <h2 className="pro-onboarding-hero-title">About your business</h2>
                <p className="pro-onboarding-hero-subtitle">These details help property owners find and reach you.</p>
                <div className="pro-onboarding-form">
                  <div className="form-group">
                    <label className="pro-onboarding-field-label">Business Name</label>
                    <input
                      type="text"
                      className="input-field"
                      value={businessName}
                      onChange={(e) => setBusinessName(e.target.value)}
                      placeholder="Your business or company name"
                    />
                  </div>
                  <div className="form-group">
                    <label className="pro-onboarding-field-label">Phone Number</label>
                    <input
                      type="tel"
                      className="input-field"
                      value={wizardPhoneNumber}
                      onChange={(e) => setWizardPhoneNumber(e.target.value)}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  <div className="form-group">
                    <label className="pro-onboarding-field-label">Service Area (City / Region)</label>
                    <AddressAutocomplete
                      value={serviceArea}
                      onChange={(addressInput) => {
                        if (typeof addressInput === 'object' && addressInput.address) {
                          setServiceArea(addressInput.address);
                        } else {
                          setServiceArea(addressInput);
                        }
                      }}
                      placeholder="Search and select city or address"
                    />
                  </div>
                  <div className="form-group">
                    <label className="pro-onboarding-field-label">Service Radius (miles)</label>
                    <input
                      type="range"
                      min="5"
                      max="100"
                      step="5"
                      value={serviceRadius}
                      onChange={(e) => setServiceRadius(e.target.value)}
                      style={{ width: '100%' }}
                    />
                    <span style={{ fontSize: 14, color: '#475569', fontWeight: 600 }}>{serviceRadius} miles</span>
                  </div>
                  <ServiceAreaMap serviceArea={serviceArea} serviceRadius={parseInt(serviceRadius, 10) || 0} />
                  <div className="form-group">
                    <label className="pro-onboarding-field-label">Service Fee per Visit ($)</label>
                    <input
                      type="number"
                      className="input-field"
                      value={serviceFee}
                      onChange={(e) => setServiceFee(e.target.value)}
                      placeholder="e.g. 100"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>
                <div className="pro-onboarding-actions">
                  <button type="button" className="pro-onboarding-back" onClick={handleWizardBack}>
                    Back
                  </button>
                  <button type="button" className="btn-primary" onClick={handleWizardNext}>
                    Continue
                  </button>
                </div>
              </div>
            )}

            {onboardingStep === 4 && (
              <div className="pro-onboarding-hero">
                <h2 className="pro-onboarding-hero-title">Refundable ${PRO_DEPOSIT_AMOUNT} deposit</h2>
                <p className="pro-onboarding-hero-subtitle">One small step before you start seeing jobs.</p>
                <div className="pro-onboarding-deposit-box">
                  <div className="pro-onboarding-deposit-amount">${PRO_DEPOSIT_AMOUNT}.00</div>
                  <p className="pro-onboarding-deposit-desc">
                    This refundable deposit covers identity verification and background screening through our licensed
                    partner. It helps us keep the marketplace safe for property owners and residents.
                  </p>
                  <ul className="pro-onboarding-deposit-list">
                    <li>
                      <strong>Not approved?</strong> Full refund.
                    </li>
                    <li>
                      <strong>Approved?</strong> Refunded after your first completed paid job.
                    </li>
                    <li>
                      <strong>Non-refundable only</strong> in narrow, terms-defined cases (e.g. verified fraud).
                    </li>
                  </ul>
                  {depositError && <p className="pro-onboarding-deposit-error">{depositError}</p>}
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={handleDepositCheckout}
                    disabled={depositLoading}
                    style={{ width: '100%', marginTop: 16 }}
                  >
                    {depositLoading ? 'Redirecting to checkout...' : `Pay $${PRO_DEPOSIT_AMOUNT} refundable deposit`}
                  </button>
                  <button
                    type="button"
                    className="pro-onboarding-skip"
                    onClick={async () => {
                      await saveWizardStep(5);
                      setOnboardingStep(5);
                    }}
                  >
                    Skip for now (you can pay from your account later)
                  </button>
                </div>
                <div className="pro-onboarding-actions">
                  <button type="button" className="pro-onboarding-back" onClick={handleWizardBack}>
                    Back
                  </button>
                </div>
              </div>
            )}

            {onboardingStep === 5 && (
              <div className="pro-onboarding-hero">
                <div className="pro-onboarding-success">
                  <div className="pro-onboarding-success-icon">🎉</div>
                  <h2 className="pro-onboarding-hero-title">You are all set</h2>
                  <p className="pro-onboarding-hero-subtitle">Here is a summary of your profile:</p>
                  <div className="pro-onboarding-summary">
                    <div className="pro-onboarding-summary-row">
                      <span className="pro-onboarding-summary-label">Business</span>
                      <span>{businessName || '—'}</span>
                    </div>
                    <div className="pro-onboarding-summary-row">
                      <span className="pro-onboarding-summary-label">Trades</span>
                      <span>{wizardTrades.join(', ') || '—'}</span>
                    </div>
                    <div className="pro-onboarding-summary-row">
                      <span className="pro-onboarding-summary-label">Service area</span>
                      <span>
                        {serviceArea || '—'} ({serviceRadius} mi)
                      </span>
                    </div>
                    <div className="pro-onboarding-summary-row">
                      <span className="pro-onboarding-summary-label">Deposit</span>
                      <span>
                        {depositPaid ? (profData?.adminLeadAccessGranted ? 'Waived (admin)' : 'Paid') : 'Pending'}
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={handleFinalSetupComplete}
                    style={{ width: '100%', marginTop: 24, padding: '16px 24px', fontSize: 16 }}
                  >
                    Open dashboard
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          // DASHBOARD TABS CONTENT
          <>
            {activeTab === 'overview' && renderOverviewDashboard()}
            {activeTab === 'jobs' && renderJobsWorkbench()}
            {activeTab === 'earnings' && renderEarningsSection()}
            {activeTab === 'profile' && renderProfileSection()}
          </>
        )}
      </div>

      {/* Floating Feedback Button */}
      <button
        onClick={() => setShowFeedbackModal(true)}
        style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          width: '56px',
          height: '56px',
          borderRadius: '50%',
          background: '#1e293b',
          color: 'white',
          border: 'none',
          cursor: 'pointer',
          fontSize: '24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 999,
          transition: 'transform 0.2s, box-shadow 0.2s',
        }}
        onMouseEnter={(e) => {
          e.target.style.transform = 'scale(1.1)';
          e.target.style.boxShadow = '0 6px 16px rgba(0,0,0,0.2)';
        }}
        onMouseLeave={(e) => {
          e.target.style.transform = 'scale(1)';
          e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
        }}
        title="Send feedback"
      >
        💬
      </button>

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <FeedbackModal 
          user={user} 
          userData={userData}
          onClose={() => setShowFeedbackModal(false)}
        />
      )}
    </div>
  );
}

export default ProfessionalDashboard;
